import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from '@/components/ui/command';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer,
  Tooltip as RTooltip,
} from 'recharts';
import { toast } from 'sonner';
import {
  CalendarRange, Download, Plus, Trash2, Save, RefreshCw, Building2,
  Pencil, Info, X, Loader2, LineChart, Users2, ShieldCheck, ChevronDown,
  BarChart3, GaugeCircle, TrendingUp, TrendingDown, Eye, EyeOff, MoreVertical,
  ChevronRight, Layers, Check, ChevronsUpDown, Wand2, Copy, Euro, ChevronLeft,
  CheckCircle2, CalendarClock, AlertTriangle, ArrowRight, History, MapPin,
} from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import api from '@/lib/api';
import { fmtInt, fmtEur, fmtDateTime } from '@/lib/format';
import { ProgrammationWizard } from '@/components/rex/ProgrammationWizard';

// --------------------------------------------------------------------------- //
//  Constantes UI                                                               //
// --------------------------------------------------------------------------- //
const MOIS_ABBR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const STATUT_CFG = {
  ferme: { label: 'Ferme', cls: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' },
  provisoire: { label: 'Provisoire', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800' },
  non_confirme: { label: 'Non confirmé', cls: 'bg-slate-200 text-slate-700 dark:bg-slate-700/60 dark:text-slate-300 border-slate-300 dark:border-slate-600' },
};

const TYPE_CFG = {
  realized: { label: 'Réalisé (figé)', cell: 'bg-slate-100 dark:bg-slate-800/50', dot: 'bg-slate-400' },
  forecast: { label: 'Prévision (mois en cours)', cell: 'bg-amber-100/70 dark:bg-amber-900/25', dot: 'bg-amber-500' },
  programmed: { label: 'Programmé (à venir)', cell: 'bg-sky-100/70 dark:bg-sky-900/25', dot: 'bg-sky-500' },
};

// Fiabilité (statut) — ordre + couleurs pour barres/graphes.
const STATUT_ORDER = ['ferme', 'provisoire', 'non_confirme'];
const STATUT_BAR = {
  ferme: '#059669',        // emerald-600
  provisoire: '#d97706',   // amber-600
  non_confirme: '#94a3b8', // slate-400
};

// Petite barre empilée de fiabilité (Ferme / Provisoire / Non confirmé).
const ReliabilityBar = ({ totals, className = '', height = 'h-2' }) => {
  const t = totals || {};
  const total = STATUT_ORDER.reduce((a, s) => a + (t[s] || 0), 0);
  if (!total) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className={`flex ${height} w-full overflow-hidden rounded-full bg-muted ${className}`}>
      {STATUT_ORDER.map((s) => (t[s] > 0 ? (
        <div key={s} style={{ width: `${(t[s] / total) * 100}%`, background: STATUT_BAR[s] }} />
      ) : null))}
    </div>
  );
};

const emptyLine = () => ({
  id: `new-${Math.random().toString(36).slice(2, 9)}`,
  canal: '', region: '', months: {}, statut: 'provisoire',
  nb_missions: '', taux_reel: '', note: '',
});

const monthVal = (line, m) => {
  const v = (line.months || {})[String(m)] ?? (line.months || {})[m];
  return typeof v === 'number' ? v : (v ? Number(v) : 0);
};

// Saisie rapide d'une ligne (12 mois) : répartir un total annuel ou appliquer la même valeur.
const QuickFill = ({ onApply, testid }) => {
  const [v, setV] = useState('');
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground" title="Saisie rapide" data-testid={testid}>
          <Wand2 className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-2" align="start">
        <p className="mb-1.5 text-xs font-medium">Saisie rapide · 12 mois</p>
        <Input type="number" value={v} onChange={(e) => setV(e.target.value)} placeholder="Volume" className="mb-2 h-8 text-xs" />
        <div className="flex flex-col gap-1.5">
          <Button size="sm" variant="outline" className="h-7 justify-start text-xs" onClick={() => { onApply('annual', v); setOpen(false); }} data-testid={testid ? `${testid}-annual` : undefined}>
            Répartir ce total sur l'année
          </Button>
          <Button size="sm" variant="outline" className="h-7 justify-start text-xs" onClick={() => { onApply('even', v); setOpen(false); }} data-testid={testid ? `${testid}-even` : undefined}>
            Même valeur chaque mois
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// --------------------------------------------------------------------------- //
//  Composant principal                                                         //
// --------------------------------------------------------------------------- //
export const ProgrammationTab = () => {
  const [meta, setMeta] = useState(null);
  const [year, setYear] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);          // { code, month }
  const [editVal, setEditVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [planFor, setPlanFor] = useState(null);    // { code, name }
  const [assocOpen, setAssocOpen] = useState(false);
  const [view, setView] = useState('matrix');      // matrix | comparison
  const [groupBy, setGroupBy] = useState('association'); // association | canal | zone
  const [metric, setMetric] = useState('volume');        // volume | revenue (chiffre d'affaires)
  const [expanded, setExpanded] = useState({});    // { [association_code]: bool }
  const [seedInfo, setSeedInfo] = useState(null);
  const [seedOpen, setSeedOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [wizardFor, setWizardFor] = useState(null);   // null | { code? } (assistant volumes)
  const [assocsList, setAssocsList] = useState([]);
  const [duplicateFor, setDuplicateFor] = useState(null); // null | {} | { code, name } (duplication d'année)
  const [priceFor, setPriceFor] = useState(null);         // null | { code, name } (tarifs par canal)
  const [todoOpen, setTodoOpen] = useState(false);        // dialog « bulletins réalisés à chiffrer »
  const [exportingProg, setExportingProg] = useState(false);  // export Excel du programme complet

  const exportProgramme = useCallback(async () => {
    if (!year) return;
    setExportingProg(true);
    try {
      const res = await api.get('/rex/programmation/export-programme',
        { params: { year }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PROGRAMME ${year}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Programme ${year} exporté en Excel`);
    } catch (e) {
      toast.error("Export impossible");
    }
    setExportingProg(false);
  }, [year]);

  const loadAssocs = useCallback(async () => {
    try {
      const { data } = await api.get('/rex/programmation/associations');
      setAssocsList(data.associations || []);
    } catch (e) { /* silencieux */ }
  }, []);

  const loadSeed = useCallback(async () => {
    try {
      const { data } = await api.get('/rex/programmation/seed-status');
      setSeedInfo(data);
    } catch (e) { /* silencieux */ }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const { data } = await api.get('/rex/programmation/meta');
      setMeta(data);
      setYear((y) => y ?? parseInt(String(data.current_ym).slice(0, 4), 10));
    } catch (e) { toast.error('Chargement des paramètres impossible'); }
  }, []);

  const loadMatrix = useCallback(async (y, silent = false) => {
    if (!y) return;
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/rex/programmation/matrix', { params: { year: y } });
      setMatrix(data);
    } catch (e) { toast.error('Chargement de la matrice impossible'); }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadSeed(); }, [loadSeed]);
  useEffect(() => { loadAssocs(); }, [loadAssocs]);
  useEffect(() => { if (year) loadMatrix(year); }, [year, loadMatrix]);

  const runSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post('/rex/programmation/seed-2027', {});
      const n = (data.loaded || []).length;
      toast.success(n > 0
        ? `Programmation 2027 chargée (${n} association${n > 1 ? 's' : ''}, ${fmtInt(data.total)} bulletins)`
        : 'Programmation 2027 déjà présente dans cet environnement');
      if (year !== 2027) setYear(2027); else loadMatrix(2027, true);
      await Promise.all([loadMeta(), loadSeed()]);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Chargement impossible'); }
    setSeeding(false);
    setSeedOpen(false);
  };

  const startEdit = (code, cell) => {
    if (cell.type === 'realized') return;
    const orig = (cell.value === null || cell.value === undefined) ? null : Math.round(Number(cell.value));
    setEdit({ code, month: cell.month, origValue: orig, origOverride: !!cell.is_override });
    setEditVal(cell.value === null || cell.value === undefined ? '' : String(cell.value));
  };

  // Ne referme que si la cellule visée est TOUJOURS celle en cours d'édition.
  // (évite d'écraser l'ouverture d'une autre cellule cliquée entre-temps)
  const closeIfCurrent = (target) => setEdit((cur) =>
    (cur && cur.code === target.code && cur.month === target.month) ? null : cur);

  const submitOverride = async () => {
    if (!edit) return;
    const target = edit;
    const raw = editVal.trim();
    const value = raw === '' ? null : Math.round(Number(raw));
    if (raw !== '' && isNaN(value)) { closeIfCurrent(target); return; }
    // Aucun changement réel → ne PAS créer d'override inutile (contour orange parasite)
    const noChange = value === null
      ? !target.origOverride                 // vider une cellule déjà « auto » = rien à faire
      : value === target.origValue;          // retaper la même valeur = rien à faire
    if (noChange) { closeIfCurrent(target); return; }
    setSaving(true);
    try {
      await api.patch('/rex/programmation/override', {
        association_code: target.code, year, month: target.month, value,
      });
      await loadMatrix(year, true);
      toast.success(value === null ? 'Correction retirée (retour à l\'auto)' : 'Volume mis à jour');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Mise à jour impossible'); }
    setSaving(false);
    closeIfCurrent(target);
  };

  // Ajoute une ligne de détail (canal × région) vide sous l'association, à remplir inline.
  const addDetailLine = async (code) => {
    setExpanded((o) => ({ ...o, [code]: true }));
    setSaving(true);
    try {
      await api.post('/rex/programmation/line-op', { association_code: code, year, op: 'add' });
      await loadMatrix(year, true);
      toast.success('Ligne ajoutée — renseignez le canal, la zone et les volumes');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Ajout impossible'); }
    setSaving(false);
  };

  // Retire TOUTES les corrections manuelles d'une association (retour au plan détaillé).
  const clearRowOverrides = async (code) => {
    setSaving(true);
    try {
      await api.post('/rex/programmation/overrides/clear', { association_code: code, year });
      await loadMatrix(year, true);
      toast.success('Corrections manuelles retirées');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    setSaving(false);
  };

  // Masque / réaffiche une association dans la matrice de l'année sélectionnée.
  const setRowHidden = async (code, hidden) => {
    setSaving(true);
    try {
      await api.patch('/rex/programmation/hidden', { association_code: code, year, hidden });
      await loadMatrix(year, true);
      toast.success(hidden ? 'Association retirée de la programmation' : 'Association réaffichée');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    setSaving(false);
  };

  const onCellKey = (e) => {
    if (e.key === 'Enter') submitOverride();
    if (e.key === 'Escape') setEdit(null);
  };

  const setAssocStatut = async (code, statut) => {
    setSaving(true);
    try {
      await api.patch('/rex/programmation/plan/statut', { association_code: code, year, statut });
      await loadMatrix(year, true);
      toast.success(`Fiabilité mise à jour : ${STATUT_CFG[statut]?.label || statut}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Mise à jour impossible'); }
    setSaving(false);
  };

  const monthsHead = matrix?.months || MOIS_ABBR.map((label, i) => ({ num: i + 1, label }));

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-4" data-testid="rex-programmation-tab">
        {/* Barre d'outils */}
        <Card className="card-shadow">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="flex items-center gap-2 text-base">
                  <LineChart className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />
                  Programmation volumétrique — bulletins livrés (BS Réel)
                </CardTitle>
                <CardDescription className="mt-1 max-w-3xl">
                  Pilotage du <b>volume</b> de bulletins par association et par mois. Historique
                  <b> réalisé</b> depuis 2023 (figé), <b>prévision</b> linéaire du mois en cours
                  (corrigeable) et <b>programmation</b> prévisionnelle des mois à venir.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <CalendarRange className="h-4 w-4 text-muted-foreground" />
                  <Select value={year ? String(year) : ''} onValueChange={(v) => setYear(parseInt(v, 10))}>
                    <SelectTrigger className="h-9 w-[110px]" data-testid="rex-prog-year-select">
                      <SelectValue placeholder="Année" />
                    </SelectTrigger>
                    <SelectContent>
                      {(meta?.years || []).map((y) => (
                        <SelectItem key={y} value={String(y)} data-testid={`rex-prog-year-${y}`}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" className="h-9" onClick={() => setWizardFor({})} data-testid="rex-prog-wizard-open"
                  style={{ background: 'var(--tenant-accent-500)', color: '#fff' }}>
                  <Wand2 className="mr-1.5 h-4 w-4" />Programmer une association
                </Button>
                <Button variant="outline" size="sm" className="h-9" onClick={() => setDuplicateFor({})} data-testid="rex-prog-duplicate-open">
                  <Copy className="mr-1.5 h-4 w-4" />Dupliquer une année
                </Button>
                <Button variant="outline" size="sm" className="h-9" onClick={() => setAssocOpen(true)} data-testid="rex-prog-manage-assocs">
                  <Users2 className="mr-1.5 h-4 w-4" />Associations
                </Button>
                <Button variant="outline" size="sm"
                  className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
                  onClick={exportProgramme} disabled={exportingProg || !year}
                  title={`Télécharger le programme ${year || ''} en Excel`}
                  data-testid="rex-prog-export-programme">
                  {exportingProg ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
                  Télécharger Excel
                </Button>
                <Button variant="outline" size="sm" className="h-9" onClick={() => loadMatrix(year)} data-testid="rex-prog-refresh">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Bandeau : programmation 2027 absente de cet environnement (ex. production) */}
        {seedInfo && !seedInfo.fully_loaded && (
          <div
            className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800 dark:bg-amber-950/40"
            data-testid="rex-prog-seed-banner"
          >
            <Info className="h-5 w-5 shrink-0 text-amber-600" />
            <span className="flex-1 text-amber-900 dark:text-amber-200">
              La <b>programmation 2027</b> n'est pas encore chargée dans cet environnement.
              Cliquez pour injecter les volumes fournis (Amnesty, Handicap International,
              Chaîne de l'Espoir, PLAN International, Vaincre la Mucoviscidose).
            </span>
            <Button
              onClick={() => setSeedOpen(true)}
              disabled={seeding}
              data-testid="rex-prog-seed-btn"
              style={{ background: 'var(--tenant-accent-500)', color: '#fff' }}
            >
              {seeding ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
              Charger la programmation 2027
            </Button>
          </div>
        )}

        <Tabs value={view} onValueChange={setView} className="w-full">
          <TabsList data-testid="rex-prog-subtabs">
            <TabsTrigger value="matrix" data-testid="rex-prog-subtab-matrix">
              <BarChart3 className="mr-1.5 h-4 w-4" />Matrice volumétrique
            </TabsTrigger>
            <TabsTrigger value="comparison" data-testid="rex-prog-subtab-comparison">
              <GaugeCircle className="mr-1.5 h-4 w-4" />Réalisé vs Programmé
            </TabsTrigger>
          </TabsList>

          <TabsContent value="matrix" className="mt-4 space-y-4">
            {/* Regroupement : par association ou par canal — même tableau, mois identiques */}
            <div className="flex flex-wrap items-center gap-2" data-testid="rex-prog-groupby">
              <span className="text-xs font-medium text-muted-foreground">Grouper par</span>
              <div className="inline-flex rounded-lg border bg-muted/40 p-0.5">
                {[{ k: 'association', label: 'Association', icon: Building2 }, { k: 'canal', label: 'Canal', icon: Layers }, { k: 'zone', label: 'Zone', icon: MapPin }].map((opt) => {
                  const active = groupBy === opt.k; const Icon = opt.icon;
                  return (
                    <button
                      key={opt.k}
                      onClick={() => setGroupBy(opt.k)}
                      className={`flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors ${active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                      data-testid={`rex-prog-groupby-${opt.k}`}
                      aria-pressed={active}
                    >
                      <Icon className="h-3.5 w-3.5" />{opt.label}
                    </button>
                  );
                })}
              </div>
              <span className="text-[11px] text-muted-foreground">
                {groupBy === 'association'
                  ? 'Dépliez une association (▸) pour voir son détail par canal × région.'
                  : groupBy === 'canal'
                  ? 'Dépliez un canal (▸) pour voir le dispatch par région, puis par association.'
                  : 'Dépliez une zone (▸) pour voir les canaux déployés, puis les associations positionnées — anticipez logistique, recrutement et responsables d\u2019équipe.'}
              </span>
              {groupBy === 'association' && (
                <div className="ml-auto flex items-center gap-2" data-testid="rex-prog-metric-toggle">
                  <span className="text-xs font-medium text-muted-foreground">Afficher</span>
                  <div className="inline-flex rounded-lg border bg-muted/40 p-0.5">
                    {[{ k: 'volume', label: 'Volume', icon: BarChart3 }, { k: 'revenue', label: "Chiffre d'affaires", icon: Euro }].map((opt) => {
                      const active = metric === opt.k; const Icon = opt.icon;
                      return (
                        <button
                          key={opt.k}
                          onClick={() => setMetric(opt.k)}
                          className="flex items-center gap-1.5 rounded-md px-3 py-1 text-xs font-medium transition-colors"
                          style={active
                            ? { background: 'var(--tenant-accent-500)', color: '#fff' }
                            : {}}
                          data-testid={`rex-prog-metric-${opt.k}`}
                          aria-pressed={active}
                        >
                          <Icon className="h-3.5 w-3.5" />{opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Légende (cellules éditables → vue Association uniquement) */}
            {groupBy === 'association' && metric === 'volume' && (
            <div className="flex flex-wrap items-center gap-4 rounded-lg border bg-muted/30 px-3 py-2 text-xs">
              {Object.entries(TYPE_CFG).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1.5" data-testid={`rex-prog-legend-${k}`}>
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${v.dot}`} />
                  <span className="text-muted-foreground">{v.label}</span>
                </span>
              ))}
              <span className="ml-auto flex items-center gap-1.5 text-muted-foreground">
                <Info className="h-3.5 w-3.5" />Cliquez une cellule Prévision / Programmé pour saisir un volume · le crayon (⋯) ouvre le détail par canal.
              </span>
            </div>
            )}
            {groupBy === 'association' && metric === 'revenue' && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs" data-testid="rex-prog-revenue-hint">
              <Euro className="h-3.5 w-3.5" style={{ color: 'var(--tenant-accent-600)' }} />
              <span className="text-muted-foreground">
                Chiffre d'affaires = <b>volume × prix convenu</b> : <b>réel</b> sur les mois écoulés, <b>prévisionnel</b> ensuite. Cliquez <b>« Prix »</b> en face d'une association pour fixer le tarif par canal (ex. <i>Rue Fixe = 8,50 €</i>).
              </span>
            </div>
            )}

            {/* Alerte : bulletins réalisés non encore chiffrés (canal à préciser / prix manquant) */}
            {metric === 'revenue' && matrix && (matrix.revenue_todo || []).length > 0 && (() => {
              const todo = matrix.revenue_todo || [];
              const vol = todo.reduce((a, t) => a + (t.volume || 0), 0);
              return (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs dark:border-amber-800 dark:bg-amber-950/30" data-testid="rex-prog-revenue-todo-banner">
                  <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <span className="text-amber-800 dark:text-amber-200">
                    <b>{fmtInt(vol)}</b> bulletin{vol > 1 ? 's' : ''} réalisé{vol > 1 ? 's' : ''} pas encore chiffré{vol > 1 ? 's' : ''} ({todo.length} point{todo.length > 1 ? 's' : ''} à régler) — canal à préciser ou tarif manquant.
                  </span>
                  <Button
                    size="sm"
                    className="ml-auto h-7 gap-1 bg-amber-600 px-2.5 text-[11px] text-white hover:bg-amber-700"
                    onClick={() => setTodoOpen(true)}
                    data-testid="rex-prog-revenue-todo-open"
                  >
                    Régler en 1 clic<ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })()}

            {/* Bandeau : associations retirées de la programmation (réaffichables) */}
            {matrix && (matrix.hidden || []).length > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2 text-xs" data-testid="rex-prog-hidden-banner">
                <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {matrix.hidden.length} association{matrix.hidden.length > 1 ? 's' : ''} retirée{matrix.hidden.length > 1 ? 's' : ''} de {matrix.year} :
                </span>
                {matrix.hidden.map((h) => (
                  <Button
                    key={h.code}
                    variant="outline"
                    size="sm"
                    className="h-6 gap-1 px-2 text-[11px]"
                    onClick={() => setRowHidden(h.code, false)}
                    data-testid={`rex-prog-unhide-${h.code}`}
                  >
                    <Eye className="h-3 w-3" />Réafficher {h.name}
                  </Button>
                ))}
              </div>
            )}

            {/* Bandeau fiabilité consolidée (volume) / résumé CA réel + prévisionnel (revenue) */}
            {matrix && matrix.total && (
              metric === 'revenue'
                ? ((matrix.total.revenue_total || 0) > 0
                   || (matrix.total.revenue_real_total || 0) > 0
                   || (matrix.total.revenue_forecast_total || 0) > 0)
                : matrix.total.programmed_total > 0
            ) && (
              <ReliabilitySummary total={matrix.total} year={matrix.year} metric={metric} />
            )}

            {/* Matrice */}
            {groupBy === 'association' && (
            <Card className="card-shadow">
          <CardContent className="p-0">
            {loading ? (
              <div className="space-y-2 p-4">
                {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
              </div>
            ) : !matrix || matrix.rows.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground" data-testid="rex-prog-empty">
                Aucune association disponible pour {year}.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="text-sm">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="sticky left-0 z-20 min-w-[190px] bg-muted shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">Association</TableHead>
                      {monthsHead.map((m) => (
                        <TableHead key={m.num} className={`${metric === 'revenue' ? 'min-w-[96px]' : 'min-w-[64px]'} text-center capitalize`}>{MOIS_ABBR[m.num - 1]}</TableHead>
                      ))}
                      <TableHead className={`${metric === 'revenue' ? 'min-w-[120px]' : 'min-w-[84px]'} text-right font-semibold`}>Total {matrix.year}</TableHead>
                      <TableHead className="min-w-[150px] text-center">Fiabilité</TableHead>
                      <TableHead className="w-[56px] text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matrix.rows.map((row) => { const rowHasOv = (row.cells || []).some((c) => c.is_override); const isExp = !!expanded[row.code]; return (
                      <React.Fragment key={row.code}>
                      <TableRow data-testid={`rex-prog-row-${row.code}`}>
                        <TableCell className="sticky left-0 z-20 bg-background font-medium shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">
                          <div className="flex items-center gap-1.5">
                            {(row.lines || []).length > 0 ? (
                              <button
                                onClick={() => setExpanded((o) => ({ ...o, [row.code]: !o[row.code] }))}
                                className="shrink-0 rounded p-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)]"
                                aria-expanded={isExp}
                                data-testid={`rex-prog-expand-${row.code}`}
                                title="Déplier le détail par canal × région"
                              >
                                <ChevronRight className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${isExp ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} />
                              </button>
                            ) : <span className="inline-block w-[22px] shrink-0" />}
                            <span className="truncate">{row.name}</span>
                            {row.is_prospect && (
                              <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px] font-normal text-muted-foreground">prospect</Badge>
                            )}
                            {metric === 'revenue' && (
                              <button
                                onClick={() => setPriceFor({ code: row.code, name: row.name })}
                                className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors hover:border-[color:var(--tenant-accent-500)] hover:text-[color:var(--tenant-accent-600)]"
                                data-testid={`rex-prog-prices-${row.code}`}
                                title="Définir le prix convenu par canal"
                              >
                                <Euro className="h-3 w-3" />Prix
                              </button>
                            )}
                          </div>
                          <span className="ml-[22px] text-[10px] text-muted-foreground">{row.code}</span>
                        </TableCell>
                        {metric === 'revenue' ? (row.revenue_cells || []).map((cell) => (
                          <TableCell key={cell.month} className="text-center tabular-nums" data-testid={`rex-prog-cell-${row.code}-${cell.month}`}>
                            {cell.value ? <span className="font-medium">{fmtEur(cell.value, 2)}</span> : <span className="text-muted-foreground opacity-40">·</span>}
                          </TableCell>
                        )) : row.cells.map((cell) => {
                          const cfg = TYPE_CFG[cell.type] || TYPE_CFG.programmed;
                          const isEditing = edit && edit.code === row.code && edit.month === cell.month;
                          const editable = cell.type !== 'realized';
                          return (
                            <TableCell
                              key={cell.month}
                              className={`text-center tabular-nums ${cfg.cell} ${editable ? 'cursor-pointer' : ''}`}
                              onClick={() => !isEditing && editable && startEdit(row.code, cell)}
                              data-testid={`rex-prog-cell-${row.code}-${cell.month}`}
                            >
                              {isEditing ? (
                                <Input
                                  autoFocus
                                  type="number"
                                  value={editVal}
                                  onChange={(e) => setEditVal(e.target.value)}
                                  onBlur={submitOverride}
                                  onKeyDown={onCellKey}
                                  className="h-7 w-full px-1 text-center text-xs"
                                  data-testid={`rex-prog-cell-input-${row.code}-${cell.month}`}
                                />
                              ) : cell.type === 'forecast' ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex items-center gap-1 rounded px-1">
                                      {fmtInt(cell.value)}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-xs">
                                      <div>Réalisé à date : <b>{fmtInt(cell.realized_mtd)}</b></div>
                                      <div>Projection auto : <b>{fmtInt(cell.auto_forecast)}</b></div>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="inline-block rounded px-1">
                                  {cell.value ? fmtInt(cell.value) : <span className="text-muted-foreground">·</span>}
                                </span>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-right font-semibold tabular-nums" style={metric === 'revenue' ? { color: 'var(--tenant-accent-600)' } : {}}>
                          {metric === 'revenue' ? fmtEur(row.revenue_total, 2) : fmtInt(row.total)}
                        </TableCell>
                        <TableCell className="px-2">
                          {row.programmed_total > 0 ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  className="group flex w-full items-center gap-1.5 rounded px-1 py-1 hover:bg-muted"
                                  data-testid={`rex-prog-statut-${row.code}`}
                                >
                                  <ReliabilityBar totals={row.status_totals} className="flex-1" />
                                  <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel className="text-xs">Fiabilité — {row.name}</DropdownMenuLabel>
                                <div className="px-2 pb-1.5 text-[11px] text-muted-foreground">
                                  {STATUT_ORDER.map((s) => (row.status_totals[s] > 0 ? (
                                    <div key={s} className="flex items-center justify-between gap-3">
                                      <span className="flex items-center gap-1.5">
                                        <span className="inline-block h-2 w-2 rounded-full" style={{ background: STATUT_BAR[s] }} />
                                        {STATUT_CFG[s].label}
                                      </span>
                                      <b className="tabular-nums text-foreground">{fmtInt(row.status_totals[s])}</b>
                                    </div>
                                  ) : null))}
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-[10px] font-normal text-muted-foreground">
                                  Tout marquer comme…
                                </DropdownMenuLabel>
                                {STATUT_ORDER.map((s) => (
                                  <DropdownMenuItem
                                    key={s}
                                    onClick={() => setAssocStatut(row.code, s)}
                                    data-testid={`rex-prog-set-statut-${row.code}-${s}`}
                                  >
                                    <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ background: STATUT_BAR[s] }} />
                                    {STATUT_CFG[s].label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : (
                            <span className="flex justify-center text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`rex-prog-actions-${row.code}`} title="Actions">
                                <MoreVertical className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60">
                              <DropdownMenuLabel className="text-xs">{row.name}</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setWizardFor({ code: row.code })}
                                data-testid={`rex-prog-wizard-row-${row.code}`}
                              >
                                <Wand2 className="mr-2 h-3.5 w-3.5" />Assistant volumes (guidé)
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setPlanFor({ code: row.code, name: row.name })}
                                data-testid={`rex-prog-edit-plan-${row.code}`}
                              >
                                <Pencil className="mr-2 h-3.5 w-3.5" />Modifier le détail (canal × région)
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setDuplicateFor({ code: row.code, name: row.name })}
                                data-testid={`rex-prog-duplicate-row-${row.code}`}
                              >
                                <Copy className="mr-2 h-3.5 w-3.5" />Dupliquer vers une autre année…
                              </DropdownMenuItem>
                              {rowHasOv && (
                                <DropdownMenuItem
                                  onClick={() => clearRowOverrides(row.code)}
                                  data-testid={`rex-prog-clear-ov-${row.code}`}
                                >
                                  <RefreshCw className="mr-2 h-3.5 w-3.5" />Réinitialiser les corrections
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setRowHidden(row.code, true)}
                                className="text-rose-600 focus:text-rose-600"
                                data-testid={`rex-prog-hide-${row.code}`}
                              >
                                <EyeOff className="mr-2 h-3.5 w-3.5" />Retirer de la programmation {matrix.year}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                      {isExp && (row.lines || []).map((ln) => (
                        <DetailLineRow
                          key={`${row.code}-${ln.id}`}
                          code={row.code}
                          year={year}
                          line={ln}
                          monthsHead={monthsHead}
                          metric={metric}
                          canaux={meta?.canaux}
                          regions={meta?.regions}
                          onRegionsChanged={loadMeta}
                          onChanged={() => loadMatrix(year, true)}
                        />
                      ))}
                      {isExp && (
                        <TableRow className="bg-muted/10" data-testid={`rex-prog-addline-row-${row.code}`}>
                          <TableCell colSpan={monthsHead.length + 4} className="py-1.5">
                            <div className="pl-9">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 gap-1 border-dashed text-xs text-muted-foreground hover:text-foreground"
                                onClick={() => addDetailLine(row.code)}
                                data-testid={`rex-prog-addline-${row.code}`}
                              >
                                <Plus className="h-3.5 w-3.5" style={{ color: 'var(--tenant-accent-600)' }} />Ajouter une ligne (canal × région)
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    ); })}
                    {/* Ligne TOTAL */}
                    <TableRow className="border-t-2 bg-muted/40 font-semibold">
                      <TableCell className="sticky left-0 z-20 bg-muted shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">TOTAL consolidé</TableCell>
                      {(metric === 'revenue' ? (matrix.total.revenue_cells || []) : matrix.total.cells).map((c) => (
                        <TableCell key={c.month} className="text-center tabular-nums">
                          {metric === 'revenue' ? (c.value ? fmtEur(c.value, 2) : <span className="text-muted-foreground opacity-40">·</span>) : fmtInt(c.value)}
                        </TableCell>
                      ))}
                      <TableCell className="text-right tabular-nums" style={{ color: 'var(--tenant-accent-600)' }}>{metric === 'revenue' ? fmtEur(matrix.total.revenue_total, 2) : fmtInt(matrix.total.total)}</TableCell>
                      <TableCell className="px-2">
                        <div className="flex flex-col gap-1">
                          <ReliabilityBar totals={matrix.total.status_totals} />
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-[10px] font-normal text-muted-foreground">
                            {STATUT_ORDER.map((s) => (matrix.total.status_totals[s] > 0 ? (
                              <span key={s} className="flex items-center gap-1">
                                <span className="inline-block h-2 w-2 rounded-full" style={{ background: STATUT_BAR[s] }} />
                                {fmtInt(matrix.total.status_totals[s])}
                              </span>
                            ) : null))}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
            </Card>
            )}

            {groupBy === 'canal' && (
            <Card className="card-shadow">
              <CardContent className="p-0">
                {loading ? (
                  <div className="space-y-2 p-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
                ) : (
                  <CanalMatrix rows={(matrix && matrix.rows) || []} monthsHead={monthsHead} year={(matrix && matrix.year) || year} />
                )}
              </CardContent>
            </Card>
            )}

            {groupBy === 'zone' && (
            <Card className="card-shadow">
              <CardContent className="p-0">
                {loading ? (
                  <div className="space-y-2 p-4">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
                ) : (
                  <RegionMatrix rows={(matrix && matrix.rows) || []} monthsHead={monthsHead} year={(matrix && matrix.year) || year} />
                )}
              </CardContent>
            </Card>
            )}
          </TabsContent>

          <TabsContent value="comparison" className="mt-4">
            <ComparisonView year={year} />
          </TabsContent>
        </Tabs>

        {saving && (
          <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border bg-background px-3 py-2 text-xs shadow-lg">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />Enregistrement…
          </div>
        )}

        {/* Assistant de programmation (guidé) */}
        {wizardFor && meta && (
          <ProgrammationWizard
            open={!!wizardFor}
            onClose={() => setWizardFor(null)}
            meta={meta}
            year={year}
            associations={assocsList}
            initialCode={wizardFor.code}
            onSaved={() => { loadMatrix(year, true); loadMeta(); loadSeed(); loadAssocs(); }}
          />
        )}

        {/* Dupliquer une année (copie 1 clic d'une programmation d'une année à l'autre) */}
        {duplicateFor && meta && (
          <DuplicateYearDialog
            open={!!duplicateFor}
            onClose={() => setDuplicateFor(null)}
            meta={meta}
            associations={assocsList}
            defaultCode={duplicateFor.code}
            defaultFromYear={year}
            onDone={(toYear) => { if (toYear) setYear(toYear); else loadMatrix(year, true); loadMeta(); loadAssocs(); }}
          />
        )}

        {/* Tarifs par canal (prix convenu par association × ANNÉE → base du chiffre d'affaires) */}
        {priceFor && meta && (
          <PriceEditorDialog
            open={!!priceFor}
            onClose={() => setPriceFor(null)}
            association={priceFor}
            canaux={meta.canaux || []}
            initialYear={year}
            onSaved={() => { loadMatrix(year, true); }}
          />
        )}

        {/* Bulletins réalisés à chiffrer : correction canal / tarif en 1 clic */}
        {todoOpen && matrix && (
          <RevenueTodoDialog
            open={todoOpen}
            onClose={() => setTodoOpen(false)}
            todo={matrix.revenue_todo || []}
            canaux={(matrix.revenue_pricing_canaux || (meta && meta.canaux) || [])}
            onSaved={() => { loadMatrix(year, true); }}
            onSetPrice={(code, name) => { setTodoOpen(false); setPriceFor({ code, name }); }}
          />
        )}

        {/* Éditeur de plan détaillé */}
        {planFor && meta && (
          <PlanEditor
            code={planFor.code}
            name={planFor.name}
            year={year}
            canaux={meta.canaux}
            statuts={meta.statuts}
            onClose={() => setPlanFor(null)}
            onSaved={() => loadMatrix(year, true)}
          />
        )}

        {/* Gestion des associations (prospects) */}
        <AssociationsDialog open={assocOpen} onClose={() => setAssocOpen(false)} onChanged={() => { loadMeta(); loadMatrix(year); loadSeed(); }} onEditPrices={(a) => { setAssocOpen(false); setPriceFor({ code: a.code, name: a.name }); }} />

        {/* Confirmation : chargement de la programmation 2027 */}
        <AlertDialog open={seedOpen} onOpenChange={setSeedOpen}>
          <AlertDialogContent data-testid="rex-prog-seed-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Charger la programmation 2027 ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action ajoute les prospects <b>PLAN International</b> et <b>Vaincre la Mucoviscidose</b>,
                puis charge les volumes 2027 : Amnesty 5 800, Handicap International 500,
                Chaîne de l'Espoir 500, PLAN 540, VLM 200 (<b>total 7 540</b>).
                Les plans déjà saisis dans cet environnement sont <b>conservés</b> (non écrasés).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={runSeed} style={{ background: 'var(--tenant-accent-500)' }} data-testid="rex-prog-seed-confirm">
                Charger
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
};

// --------------------------------------------------------------------------- //
//  Dialog : dupliquer la programmation d'une année vers une autre              //
// --------------------------------------------------------------------------- //
const DuplicateYearDialog = ({ open, onClose, meta, associations, defaultCode, defaultFromYear, onDone }) => {
  const [code, setCode] = useState(defaultCode || '');
  const [fromYear, setFromYear] = useState(defaultFromYear || null);
  const [toYear, setToYear] = useState(null);
  const [growth, setGrowth] = useState('');
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(null); // nb de lignes existantes (409)
  const [assocPop, setAssocPop] = useState(false);

  const yearOpts = useMemo(() => {
    const base = (meta?.years || []).slice();
    const max = Math.max(...(base.length ? base : [new Date().getFullYear()]), new Date().getFullYear());
    const set = new Set(base);
    for (let y = max; y <= max + 2; y += 1) set.add(y);
    return [...set].sort((a, b) => a - b);
  }, [meta]);

  useEffect(() => {
    if (open) {
      const years = meta?.years || [];
      setCode(defaultCode || '');
      setFromYear(defaultFromYear || years[years.length - 1] || null);
      setToYear(defaultFromYear ? defaultFromYear + 1 : null);
      setGrowth(''); setConflict(null); setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const assocName = (associations || []).find((a) => a.code === code)?.name;
  const sameYear = fromYear && toYear && Number(fromYear) === Number(toYear);
  const canRun = code && fromYear && toYear && !sameYear && !busy;

  const run = async (overwrite) => {
    if (!canRun) return;
    setBusy(true);
    try {
      const { data } = await api.post('/rex/programmation/duplicate', {
        association_code: code, from_year: Number(fromYear), to_year: Number(toYear),
        growth_pct: Number(growth) || 0, overwrite: !!overwrite,
      });
      toast.success(`Programmation ${data.from_year} copiée vers ${data.to_year} · ${data.lines} ligne(s) · ${fmtInt(data.total)} bulletins${data.growth_pct ? ` (${data.growth_pct > 0 ? '+' : ''}${data.growth_pct}%)` : ''}`);
      onDone && onDone(Number(toYear));
      onClose();
    } catch (e) {
      if (e?.response?.status === 409) {
        const m = /(\d+)\s+ligne/.exec(e?.response?.data?.detail || '');
        setConflict(m ? Number(m[1]) : true);
      } else {
        toast.error(e?.response?.data?.detail || 'Duplication impossible');
      }
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[520px]" data-testid="rex-prog-duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />Dupliquer une année
          </DialogTitle>
          <DialogDescription>Copiez la programmation détaillée (canaux × zones × mois) d'une association d'une année vers une autre, avec un coefficient de croissance optionnel.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Association */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Association</label>
            <Popover open={assocPop} onOpenChange={setAssocPop}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" className="w-full justify-between" data-testid="rex-prog-duplicate-assoc">
                  {assocName ? `${assocName} (${code})` : 'Sélectionner une association…'}
                  <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Rechercher…" />
                  <CommandList>
                    <CommandEmpty>Aucune association.</CommandEmpty>
                    <CommandGroup>
                      {(associations || []).map((a) => (
                        <CommandItem key={a.code} value={`${a.name} ${a.code}`} onSelect={() => { setCode(a.code); setConflict(null); setAssocPop(false); }} data-testid={`rex-prog-duplicate-assoc-${a.code}`}>
                          <Check className={`mr-2 h-4 w-4 ${code === a.code ? 'opacity-100' : 'opacity-0'}`} />
                          <span className="flex-1">{a.name}</span>
                          {a.is_prospect && <Badge variant="outline" className="ml-2 h-5 px-1.5 text-[10px]">prospect</Badge>}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* De -> Vers */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Année source</label>
              <Select value={fromYear ? String(fromYear) : ''} onValueChange={(v) => { setFromYear(Number(v)); setConflict(null); }}>
                <SelectTrigger className="h-9" data-testid="rex-prog-duplicate-from"><SelectValue placeholder="Année" /></SelectTrigger>
                <SelectContent>{yearOpts.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <ChevronRight className="mb-2.5 h-4 w-4 text-muted-foreground" />
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Année cible</label>
              <Select value={toYear ? String(toYear) : ''} onValueChange={(v) => { setToYear(Number(v)); setConflict(null); }}>
                <SelectTrigger className="h-9" data-testid="rex-prog-duplicate-to"><SelectValue placeholder="Année" /></SelectTrigger>
                <SelectContent>{yearOpts.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {sameYear && <p className="text-xs text-rose-600" data-testid="rex-prog-duplicate-sameyear">L'année source et l'année cible doivent être différentes.</p>}

          {/* Croissance */}
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Coefficient de croissance (optionnel)</label>
            <div className="flex items-center gap-2">
              <Input type="number" value={growth} onChange={(e) => setGrowth(e.target.value)} placeholder="0" className="h-9 w-28 text-center" data-testid="rex-prog-duplicate-growth" />
              <span className="text-sm text-muted-foreground">% appliqué à chaque volume mensuel (ex. 10 = +10 %)</span>
            </div>
          </div>

          {/* Conflit : année cible déjà remplie */}
          {conflict && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm dark:border-amber-800 dark:bg-amber-950/40" data-testid="rex-prog-duplicate-conflict">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <span className="text-amber-900 dark:text-amber-200">
                L'année <b>{toYear}</b> contient déjà une programmation{typeof conflict === 'number' ? ` (${conflict} ligne${conflict > 1 ? 's' : ''})` : ''}. L'écrasement est <b>irréversible</b>.
              </span>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="rex-prog-duplicate-cancel">Annuler</Button>
          {conflict ? (
            <Button onClick={() => run(true)} disabled={!canRun} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="rex-prog-duplicate-overwrite">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Copy className="mr-1.5 h-4 w-4" />}Écraser et dupliquer
            </Button>
          ) : (
            <Button onClick={() => run(false)} disabled={!canRun} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid="rex-prog-duplicate-run">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Copy className="mr-1.5 h-4 w-4" />}Dupliquer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// --------------------------------------------------------------------------- //
//  Éditeur de programmation détaillée (canal × région)                         //
// --------------------------------------------------------------------------- //
const PlanEditor = ({ code, name, year, canaux, statuts, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lines, setLines] = useState([]);
  const [realized, setRealized] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/rex/programmation/plan', { params: { association_code: code, year } });
      const pl = data.plan || {};
      setLines((pl.lines || []).map((l) => ({ ...l, nb_missions: l.nb_missions ?? '', taux_reel: l.taux_reel ?? '' })));
      setRealized(data.realized || {});
    } catch (e) { toast.error('Chargement du plan impossible'); }
    setLoading(false);
  }, [code, year]);

  useEffect(() => { load(); }, [load]);

  const updLine = (id, patch) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const updMonth = (id, m, val) => setLines((ls) => ls.map((l) => {
    if (l.id !== id) return l;
    const months = { ...(l.months || {}) };
    if (val === '' || val === null) delete months[String(m)];
    else months[String(m)] = Math.round(Number(val)) || 0;
    return { ...l, months };
  }));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const delLine = (id) => setLines((ls) => ls.filter((l) => l.id !== id));
  const quickFillLine = (id, mode, raw) => {
    const v = Math.round(Number(raw) || 0);
    setLines((ls) => ls.map((l) => {
      if (l.id !== id) return l;
      const months = {};
      if (v) {
        if (mode === 'even') { for (let m = 1; m <= 12; m += 1) months[String(m)] = v; }
        else { const base = Math.floor(v / 12); let rem = v - base * 12; for (let m = 1; m <= 12; m += 1) { months[String(m)] = base + (rem > 0 ? 1 : 0); if (rem > 0) rem -= 1; } }
      }
      return { ...l, months };
    }));
  };

  const colTotals = useMemo(() => {
    const t = {}; let grand = 0;
    for (let m = 1; m <= 12; m += 1) {
      const s = lines.reduce((acc, l) => acc + monthVal(l, m), 0);
      t[m] = s; grand += s;
    }
    return { t, grand };
  }, [lines]);

  const lineTotal = (l) => { let s = 0; for (let m = 1; m <= 12; m += 1) s += monthVal(l, m); return s; };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        association_code: code, year,
        lines: lines.map((l) => ({
          id: l.id.startsWith('new-') ? undefined : l.id,
          canal: l.canal, region: l.region, months: l.months, statut: l.statut,
          nb_missions: l.nb_missions === '' ? null : l.nb_missions,
          taux_reel: l.taux_reel === '' ? null : l.taux_reel,
          note: l.note,
        })),
        forecast_overrides: {},
      };
      await api.put('/rex/programmation/plan', payload);
      toast.success('Programmation enregistrée (le plan détaillé fait foi)');
      onSaved && onSaved();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    setSaving(false);
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const res = await api.get('/rex/programmation/export', { params: { association_code: code, year }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `PROGRAMMATION ${code} ${year}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('Export Excel généré');
    } catch (e) { toast.error('Export impossible'); }
    setExporting(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[92vh] max-w-[95vw] overflow-y-auto sm:max-w-[1180px]" data-testid="rex-prog-plan-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />
            Programmation {year} — {name} <span className="text-sm font-normal text-muted-foreground">({code})</span>
          </DialogTitle>
          <DialogDescription>
            Détaillez le volume prévisionnel par ligne <b>canal × région</b>, mois par mois, avec un statut de fiabilité.
            Les totaux mensuels alimentent la matrice (mois à venir). Utilisez la corbeille pour supprimer une ligne.
            <span className="mt-1 block text-[11px]">À l'enregistrement, ce plan détaillé <b>fait foi</b> : les éventuelles corrections manuelles saisies directement dans la matrice sont remplacées.</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="space-y-2 py-6">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}</div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="min-w-[140px]">Canal</TableHead>
                  <TableHead className="min-w-[120px]">Région / Zone</TableHead>
                  {MOIS_ABBR.map((m, i) => <TableHead key={i} className="min-w-[52px] text-center capitalize">{m}</TableHead>)}
                  <TableHead className="min-w-[64px] text-right">Total</TableHead>
                  <TableHead className="min-w-[92px] text-center">Nb miss.</TableHead>
                  <TableHead className="min-w-[92px] text-center">Taux réel</TableHead>
                  <TableHead className="min-w-[130px]">Statut</TableHead>
                  <TableHead className="w-[40px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Rappel du réalisé (lecture seule) */}
                <TableRow className="bg-slate-50 dark:bg-slate-800/40 text-muted-foreground">
                  <TableCell className="font-medium" colSpan={2}>Réalisé {year} (réf.)</TableCell>
                  {MOIS_ABBR.map((_, i) => (
                    <TableCell key={i} className="text-center tabular-nums">{realized[String(i + 1)] ? fmtInt(realized[String(i + 1)]) : '·'}</TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums">{fmtInt(Object.values(realized).reduce((a, b) => a + (b || 0), 0))}</TableCell>
                  <TableCell colSpan={3} />
                </TableRow>

                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={18} className="py-6 text-center text-muted-foreground">
                      Aucune ligne. Ajoutez une première ligne canal × région ci-dessous.
                    </TableCell>
                  </TableRow>
                )}

                {lines.map((l) => (
                  <TableRow key={l.id} data-testid={`rex-prog-line-${l.id}`}>
                    <TableCell>
                      <Select value={l.canal || undefined} onValueChange={(v) => updLine(l.id, { canal: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`rex-prog-line-canal-${l.id}`}><SelectValue placeholder="Canal…" /></SelectTrigger>
                        <SelectContent>
                          {canaux.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Input value={l.region} onChange={(e) => updLine(l.id, { region: e.target.value })}
                          placeholder="Région…" className="h-8 text-xs" data-testid={`rex-prog-line-region-${l.id}`} />
                        <QuickFill onApply={(mode, val) => quickFillLine(l.id, mode, val)} testid={`rex-prog-line-quickfill-${l.id}`} />
                      </div>
                    </TableCell>
                    {MOIS_ABBR.map((_, i) => {
                      const m = i + 1;
                      return (
                        <TableCell key={m} className="p-0.5">
                          <Input type="number" value={(l.months || {})[String(m)] ?? ''} onChange={(e) => updMonth(l.id, m, e.target.value)}
                            className="h-8 w-full px-1 text-center text-xs tabular-nums" data-testid={`rex-prog-line-m${m}-${l.id}`} />
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-right font-semibold tabular-nums">{fmtInt(lineTotal(l))}</TableCell>
                    <TableCell className="p-0.5">
                      <Input type="number" value={l.nb_missions} onChange={(e) => updLine(l.id, { nb_missions: e.target.value })}
                        className="h-8 w-full px-1 text-center text-xs" />
                    </TableCell>
                    <TableCell className="p-0.5">
                      <Input type="number" step="0.01" value={l.taux_reel} onChange={(e) => updLine(l.id, { taux_reel: e.target.value })}
                        className="h-8 w-full px-1 text-center text-xs" />
                    </TableCell>
                    <TableCell>
                      <Select value={l.statut} onValueChange={(v) => updLine(l.id, { statut: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`rex-prog-line-statut-${l.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {statuts.map((s) => <SelectItem key={s} value={s}>{STATUT_CFG[s]?.label || s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-600" onClick={() => delLine(l.id)} data-testid={`rex-prog-line-del-${l.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}

                {/* Ligne TOTAL programmé */}
                <TableRow className="border-t-2 bg-muted/40 font-semibold">
                  <TableCell colSpan={2}>TOTAL programmé</TableCell>
                  {MOIS_ABBR.map((_, i) => (
                    <TableCell key={i} className="text-center tabular-nums">{colTotals.t[i + 1] ? fmtInt(colTotals.t[i + 1]) : '·'}</TableCell>
                  ))}
                  <TableCell className="text-right tabular-nums" style={{ color: 'var(--tenant-accent-600)' }}>{fmtInt(colTotals.grand)}</TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}

        <div className="mt-2">
          <Button variant="outline" size="sm" onClick={addLine} data-testid="rex-prog-add-line">
            <Plus className="mr-1.5 h-4 w-4" />Ajouter une ligne
          </Button>
        </div>

        <DialogFooter className="mt-2 flex-wrap gap-2 sm:justify-between">
          <Button variant="outline" onClick={exportXlsx} disabled={exporting} data-testid="rex-prog-export">
            {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}Export Excel
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}><X className="mr-1.5 h-4 w-4" />Fermer</Button>
            <Button onClick={save} disabled={saving} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid="rex-prog-save-plan">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}Enregistrer
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --------------------------------------------------------------------------- //
//  Gestion des associations (prospects sans historique)                        //
// --------------------------------------------------------------------------- //
const AssociationsDialog = ({ open, onClose, onChanged, onEditPrices }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [editing, setEditing] = useState(null);   // code en cours d'édition
  const [editName, setEditName] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/rex/programmation/associations');
      setList(data.associations || []);
    } catch (e) { toast.error('Chargement des associations impossible'); }
    setLoading(false);
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const add = async () => {
    if (!code.trim() || !name.trim()) { toast.error('Code et nom requis'); return; }
    setBusy(true);
    try {
      await api.post('/rex/programmation/associations', { code: code.trim(), name: name.trim() });
      toast.success(`Association « ${name.trim()} » ajoutée`);
      setCode(''); setName('');
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Ajout impossible'); }
    setBusy(false);
  };

  const remove = async (c) => {
    setBusy(true);
    try {
      await api.delete(`/rex/programmation/associations/${encodeURIComponent(c)}`);
      toast.success('Association retirée');
      await load(); onChanged && onChanged();
    } catch (e) { toast.error('Suppression impossible'); }
    setBusy(false);
    setToDelete(null);
  };

  const startEdit = (a) => { setEditing(a.code); setEditName(a.name); };
  const cancelEdit = () => { setEditing(null); setEditName(''); };
  const saveEdit = async (a) => {
    const nn = editName.trim();
    if (!nn) { toast.error('Le nom ne peut pas être vide'); return; }
    if (nn === a.name) { cancelEdit(); return; }
    setBusy(true);
    try {
      await api.patch(`/rex/programmation/associations/${encodeURIComponent(a.code)}`, { name: nn });
      toast.success(`Renommée en « ${nn} »`);
      cancelEdit();
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Renommage impossible'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="rex-prog-assocs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users2 className="h-4 w-4" />Associations pilotées</DialogTitle>
          <DialogDescription>
            Ajoutez des associations <b>prospects</b> (sans historique, ex. pour préparer 2027),
            ou <b>renommez</b> n'importe quelle association (le nouveau nom s'applique aussitôt à toute la programmation).
            Les associations avec données réelles ne sont pas supprimables.
          </DialogDescription>
        </DialogHeader>

        {/* Formulaire d'ajout */}
        <div className="flex flex-wrap items-end gap-2 rounded-lg border bg-muted/30 p-3">
          <div className="flex-1">
            <label className="mb-1 block text-xs text-muted-foreground">Code</label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="VLM" className="h-9" data-testid="rex-prog-assoc-code" />
          </div>
          <div className="flex-[2]">
            <label className="mb-1 block text-xs text-muted-foreground">Nom</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Vaincre la Mucoviscidose" className="h-9" data-testid="rex-prog-assoc-name" />
          </div>
          <Button onClick={add} disabled={busy} className="h-9" data-testid="rex-prog-assoc-add" style={{ background: 'var(--tenant-accent-500)', color: '#fff' }}>
            <Plus className="mr-1.5 h-4 w-4" />Ajouter
          </Button>
        </div>

        {/* Liste */}
        <div className="max-h-[320px] space-y-1.5 overflow-y-auto">
          {loading ? (
            [...Array(4)].map((_, i) => <Skeleton key={i} className="h-10 w-full" />)
          ) : list.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">Aucune association.</div>
          ) : list.map((a) => (
            <div key={a.code} className="flex items-center justify-between gap-2 rounded-lg border px-3 py-2" data-testid={`rex-prog-assoc-item-${a.code}`}>
              {editing === a.code ? (
                <>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(a); if (e.key === 'Escape') cancelEdit(); }}
                    autoFocus
                    className="h-8 flex-1 text-sm"
                    data-testid={`rex-prog-assoc-edit-input-${a.code}`}
                  />
                  <span className="shrink-0 text-xs text-muted-foreground">{a.code}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button size="icon" className="h-7 w-7" onClick={() => saveEdit(a)} disabled={busy} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid={`rex-prog-assoc-edit-save-${a.code}`}>
                      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" onClick={cancelEdit} data-testid={`rex-prog-assoc-edit-cancel-${a.code}`}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-medium">{a.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{a.code}</span>
                    {a.is_prospect
                      ? <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px] font-normal">prospect</Badge>
                      : <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px] font-normal text-emerald-700 dark:text-emerald-400">données réelles</Badge>}
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => startEdit(a)} data-testid={`rex-prog-assoc-edit-${a.code}`} title="Renommer">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-[color:var(--tenant-accent-600)]" onClick={() => onEditPrices && onEditPrices(a)} data-testid={`rex-prog-assoc-prices-${a.code}`} title="Définir les tarifs par canal">
                      <Euro className="h-3.5 w-3.5" />
                    </Button>
                    {a.is_prospect && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-600" onClick={() => setToDelete(a)} data-testid={`rex-prog-assoc-del-${a.code}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer cette association ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {toDelete?.name} » ({toDelete?.code}) sera retirée de la programmation. Cette action n'affecte aucune donnée réelle.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => remove(toDelete.code)} className="bg-rose-600 hover:bg-rose-700">Retirer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default ProgrammationTab;

// --------------------------------------------------------------------------- //
//  Bandeau fiabilité consolidée (haut de la matrice)                           //
// --------------------------------------------------------------------------- //
// --------------------------------------------------------------------------- //
//  Modale : tarifs par canal d'une association (base du chiffre d'affaires)    //
// --------------------------------------------------------------------------- //
const PriceEditorDialog = ({ open, onClose, association, canaux, initialYear, onSaved }) => {
  const [year, setYear] = useState(initialYear);
  const [prices, setPrices] = useState({});
  const [prevPrices, setPrevPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { if (open) setYear(initialYear); }, [open, initialYear]);

  const loadHistory = useCallback((code) => {
    api.get('/rex/programmation/prices/history', { params: { association_code: code } })
      .then(({ data }) => setHistory(data.history || []))
      .catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    if (!open || !association?.code || !year) return undefined;
    let active = true;
    setLoading(true);
    api.get('/rex/programmation/prices', { params: { association_code: association.code, year } })
      .then(({ data }) => { if (active) { setPrices(data.prices || {}); setPrevPrices(data.prev_prices || {}); } })
      .catch(() => { if (active) { setPrices({}); setPrevPrices({}); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [open, association, year]);

  useEffect(() => { if (open && association?.code) loadHistory(association.code); }, [open, association, loadHistory]);

  const setOne = (canal, v) => setPrices((p) => ({ ...p, [canal]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const clean = {};
      (canaux || []).forEach((c) => {
        const raw = String(prices[c] ?? '').replace(',', '.').trim();
        clean[c] = raw === '' ? 0 : Number(raw);
      });
      await api.put('/rex/programmation/prices', { association_code: association.code, year, prices: clean });
      toast.success(`Tarifs ${year} enregistrés · le chiffre d'affaires est recalculé`);
      loadHistory(association.code);
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    setSaving(false);
  };

  const copyPrev = () => {
    setPrices((p) => {
      const next = { ...p };
      (canaux || []).forEach((c) => { if (Number(prevPrices[c] || 0) > 0) next[c] = prevPrices[c]; });
      return next;
    });
    toast.info(`Tarifs ${year - 1} copiés — pensez à enregistrer`);
  };

  const pctFor = (c) => {
    const cur = Number(String(prices[c] ?? '').replace(',', '.')) || 0;
    const prev = Number(prevPrices[c] || 0);
    if (!prev || !cur) return null;
    return Math.round(((cur - prev) / prev) * 1000) / 10;
  };

  const hasPrev = (canaux || []).some((c) => Number(prevPrices[c] || 0) > 0);
  const setCount = (canaux || []).filter((c) => Number(String(prices[c] ?? '').replace(',', '.')) > 0).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[480px]" data-testid="rex-prog-price-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Euro className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />Tarifs par canal — {association?.name}
          </DialogTitle>
          <DialogDescription>
            Prix convenu <b>par bulletin livré</b>, par canal et <b>par année</b>. Le chiffre d'affaires
            se calcule ensuite automatiquement (volume × prix), mois par mois.
          </DialogDescription>
        </DialogHeader>

        {/* Navigation par année : consulter / éditer les tarifs d'autres années */}
        <div className="flex items-center justify-center gap-3" data-testid="rex-prog-price-year-nav">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setYear((y) => y - 1)} data-testid="rex-prog-price-year-prev" title={`Voir ${year - 1}`}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1.5 rounded-md border px-3 py-1 text-sm font-semibold" style={{ borderColor: 'var(--tenant-accent-300)' }}>
            <CalendarRange className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />Tarifs <span data-testid="rex-prog-price-year">{year}</span>
          </div>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setYear((y) => y + 1)} data-testid="rex-prog-price-year-next" title={`Voir ${year + 1}`}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />Chargement des tarifs {year}…
          </div>
        ) : (
          <div className="space-y-2 py-1">
            {hasPrev && (
              <div className="flex items-center justify-between rounded-md bg-muted/40 px-2.5 py-1.5 text-[11px]">
                <span className="text-muted-foreground">Évolution affichée vs <b>{year - 1}</b></span>
                <Button variant="ghost" size="sm" className="h-6 gap-1 px-2 text-[11px]" onClick={copyPrev} data-testid="rex-prog-price-copy-prev">
                  <Copy className="h-3 w-3" />Copier les tarifs {year - 1}
                </Button>
              </div>
            )}
            {(canaux || []).map((c) => {
              const pct = pctFor(c);
              const prev = Number(prevPrices[c] || 0);
              return (
                <div key={c} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2" data-testid={`rex-prog-price-row-${c}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium">{c}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {prev > 0 ? `${year - 1} : ${fmtEur(prev, 2)}` : `aucun tarif ${year - 1}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {pct !== null && (
                      <Badge
                        variant="outline"
                        className={`h-5 px-1.5 text-[10px] tabular-nums ${pct > 0 ? 'border-emerald-300 text-emerald-600 dark:text-emerald-400' : pct < 0 ? 'border-rose-300 text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}`}
                        data-testid={`rex-prog-price-pct-${c}`}
                        title={`Évolution vs ${year - 1}`}
                      >
                        {pct > 0 ? '+' : ''}{pct}%
                      </Badge>
                    )}
                    <Input
                      type="number" step="0.01" min="0"
                      value={prices[c] ?? ''}
                      onChange={(e) => setOne(c, e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
                      placeholder="0,00"
                      className="h-9 w-[104px] text-right tabular-nums"
                      data-testid={`rex-prog-price-input-${c}`}
                    />
                    <span className="text-sm text-muted-foreground">€ / BS</span>
                  </div>
                </div>
              );
            })}
            {(!canaux || canaux.length === 0) && (
              <p className="py-3 text-center text-sm text-muted-foreground">Aucun canal disponible.</p>
            )}
            {/* Historique des tarifs : évolution (année, prix avant → après, %, date) */}
            {history.length > 0 && (
              <div className="pt-1">
                <button
                  type="button"
                  onClick={() => setShowHistory((s) => !s)}
                  className="flex w-full items-center gap-1.5 rounded-md px-1 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                  data-testid="rex-prog-price-history-toggle"
                >
                  <History className="h-3.5 w-3.5" />
                  Historique des tarifs ({history.length})
                  <ChevronRight className={`ml-auto h-3.5 w-3.5 transition-transform ${showHistory ? 'rotate-90' : ''}`} />
                </button>
                {showHistory && (
                  <div className="mt-1 max-h-44 space-y-1 overflow-y-auto rounded-lg border bg-muted/20 p-2" data-testid="rex-prog-price-history">
                    {history.map((h, i) => (
                      <div key={h.id || i} className="flex items-center gap-1.5 text-[11px]" data-testid="rex-prog-price-history-row">
                        <Badge className="h-5 shrink-0 px-1.5 text-[10px] font-semibold" style={{ background: 'var(--tenant-accent-100)', color: 'var(--tenant-accent-700)' }}>{h.year}</Badge>
                        <Badge variant="outline" className="h-5 shrink-0 px-1.5 text-[10px] font-normal">{h.canal}</Badge>
                        <span className="tabular-nums text-muted-foreground">{fmtEur(h.old_price || 0, 2)}</span>
                        <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                        <span className="tabular-nums font-medium" style={{ color: 'var(--tenant-accent-600)' }}>{fmtEur(h.new_price || 0, 2)}</span>
                        {(h.pct !== null && h.pct !== undefined) && (
                          <span className={`shrink-0 tabular-nums ${h.pct > 0 ? 'text-emerald-600 dark:text-emerald-400' : h.pct < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}`}>
                            ({h.pct > 0 ? '+' : ''}{h.pct}%)
                          </span>
                        )}
                        <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">{h.changed_at ? fmtDateTime(h.changed_at) : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter className="items-center gap-2">
          <span className="mr-auto text-xs text-muted-foreground">{setCount} canal(aux) tarifé(s) · {year}</span>
          <Button variant="ghost" onClick={onClose} data-testid="rex-prog-price-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving || loading} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid="rex-prog-price-save">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}Enregistrer les tarifs {year}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --------------------------------------------------------------------------- //
//  Modale : bulletins réalisés « à chiffrer » (canal à préciser / prix manquant)//
//  Correction en 1 clic — zéro estimation, rien n'est exclu du CA.             //
// --------------------------------------------------------------------------- //
const RevenueTodoDialog = ({ open, onClose, todo, canaux, onSaved, onSetPrice }) => {
  const [mapSel, setMapSel] = useState({});   // raw_canal -> canal tarifé choisi
  const [saving, setSaving] = useState(false);

  // Pré-remplit une correspondance évidente (ex. « Rue … » → Rue Fixe) pour éviter la ressaisie.
  const guessCanal = useCallback((raw, options) => {
    const r = (raw || '').toLowerCase();
    const has = (name) => (options || []).includes(name);
    if (r.includes('site') && has('Sites Privés')) return 'Sites Privés';
    if (r.includes('porte') && has('Porte-à-Porte')) return 'Porte-à-Porte';
    if (r.includes('itin') && has('Rue Itinérance')) return 'Rue Itinérance';
    if (r.includes('rue') && has('Rue Fixe')) return 'Rue Fixe';
    return undefined;
  }, []);

  useEffect(() => {
    if (!open) return;
    const init = {};
    (todo || []).forEach((t) => {
      if (t.reason === 'no_mapping' && !(t.raw_canal in init)) {
        const g = guessCanal(t.raw_canal, canaux);
        if (g) init[t.raw_canal] = g;
      }
    });
    setMapSel(init);
  }, [open, todo, canaux, guessCanal]);

  // Regroupe les canaux réalisés « à préciser » (no_mapping), dédupliqués par libellé.
  const needMapping = [];
  const seen = new Set();
  (todo || []).forEach((t) => {
    if (t.reason === 'no_mapping' && !seen.has(t.raw_canal)) {
      seen.add(t.raw_canal);
      const vol = (todo || []).filter((x) => x.raw_canal === t.raw_canal && x.reason === 'no_mapping')
        .reduce((a, x) => a + (x.volume || 0), 0);
      needMapping.push({ raw_canal: t.raw_canal, volume: vol });
    }
  });
  // Points « prix manquant » : canal identifié mais pas de tarif pour cette association.
  const needPrice = (todo || []).filter((t) => t.reason === 'no_price');

  const saveMapping = async () => {
    const map = {};
    Object.entries(mapSel).forEach(([raw, pc]) => { if (pc) map[raw] = pc; });
    if (Object.keys(map).length === 0) { toast.error('Choisissez au moins un canal'); return; }
    setSaving(true);
    try {
      await api.put('/rex/programmation/canal-map', { map });
      toast.success('Canaux précisés · le chiffre d\'affaires réel est recalculé');
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Enregistrement impossible'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-[560px]" data-testid="rex-prog-revenue-todo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />Bulletins réalisés à chiffrer
          </DialogTitle>
          <DialogDescription>
            Pour un chiffre d'affaires réel exact, précisez le canal tarifé des volumes
            réalisés ci-dessous, ou définissez le tarif manquant. <b>Aucune estimation</b> :
            rien n'est chiffré tant que l'information n'est pas fournie.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[55vh] space-y-4 overflow-y-auto py-1">
          {/* 1) Canal à préciser (ex. « Rue » → Rue Fixe / Rue Itinérance) */}
          {needMapping.length > 0 && (
            <div className="space-y-2" data-testid="rex-prog-todo-mapping">
              <p className="text-xs font-semibold text-muted-foreground">Canal à préciser</p>
              {needMapping.map((m) => (
                <div key={m.raw_canal} className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2" data-testid={`rex-prog-todo-map-${m.raw_canal}`}>
                  <Badge variant="outline" className="h-6">{m.raw_canal}</Badge>
                  <span className="text-xs text-muted-foreground"><b className="tabular-nums text-foreground">{fmtInt(m.volume)}</b> BS réalisés</span>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                  <Select value={mapSel[m.raw_canal] || undefined} onValueChange={(v) => setMapSel((s) => ({ ...s, [m.raw_canal]: v }))}>
                    <SelectTrigger className="ml-auto h-8 w-[200px]" data-testid={`rex-prog-todo-select-${m.raw_canal}`}>
                      <SelectValue placeholder="Choisir le canal tarifé…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(canaux || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          {/* 2) Tarif manquant (canal identifié mais pas de prix pour l'association) */}
          {needPrice.length > 0 && (
            <div className="space-y-2" data-testid="rex-prog-todo-price">
              <p className="text-xs font-semibold text-muted-foreground">Tarif manquant</p>
              {needPrice.map((t, i) => (
                <div key={`${t.association_code}-${t.raw_canal}-${i}`} className="flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2" data-testid={`rex-prog-todo-price-${t.association_code}-${i}`}>
                  <span className="text-sm font-medium">{t.association_name}</span>
                  <Badge variant="outline" className="h-6">{t.resolved_canal || t.raw_canal}</Badge>
                  <span className="text-xs text-muted-foreground"><b className="tabular-nums text-foreground">{fmtInt(t.volume)}</b> BS réalisés</span>
                  <Button size="sm" variant="outline" className="ml-auto h-8 gap-1 text-xs" onClick={() => onSetPrice(t.association_code, t.association_name)} data-testid={`rex-prog-todo-setprice-${t.association_code}-${i}`}>
                    <Euro className="h-3.5 w-3.5" />Définir le tarif
                  </Button>
                </div>
              ))}
            </div>
          )}

          {needMapping.length === 0 && needPrice.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">Tout est chiffré — rien à régler.</p>
          )}
        </div>

        <DialogFooter className="items-center gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="rex-prog-todo-close">Fermer</Button>
          {needMapping.length > 0 && (
            <Button onClick={saveMapping} disabled={saving} style={{ background: 'var(--tenant-accent-500)', color: '#fff' }} data-testid="rex-prog-todo-save">
              {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />}Enregistrer les canaux
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReliabilitySummary = ({ total, year, metric = 'volume' }) => {
  const st = total.status_totals || {};
  const prog = total.programmed_total || 0;
  const pct = (s) => (prog ? Math.round(((st[s] || 0) / prog) * 100) : 0);
  if (metric === 'revenue') {
    const ca = total.revenue_total || 0;
    const caReal = total.revenue_real_total || 0;
    const caFcst = total.revenue_forecast_total || 0;
    const fcstLo = caFcst * 0.7;
    const fcstHi = caFcst * 1.3;
    return (
      <Card className="card-shadow" data-testid="rex-prog-revenue-summary">
        <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-3 py-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: 'var(--tenant-accent-100)' }}>
              <Euro className="h-5 w-5" style={{ color: 'var(--tenant-accent-600)' }} />
            </span>
            <div>
              <div className="text-xs text-muted-foreground">Chiffre d'affaires {year}</div>
              <div className="text-xl font-semibold tabular-nums" style={{ color: 'var(--tenant-accent-600)' }} data-testid="rex-prog-ca-total">
                {fmtEur(ca, 2)}
              </div>
            </div>
          </div>
          <Separator orientation="vertical" className="hidden h-11 sm:block" />
          <div className="flex items-center gap-8">
            <div data-testid="rex-prog-ca-real-wrap">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CheckCircle2 className="h-3.5 w-3.5" style={{ color: STATUT_BAR.ferme }} />
                CA réel <span className="opacity-70">· mois écoulés</span>
              </div>
              <div className="mt-0.5 text-lg font-semibold tabular-nums" data-testid="rex-prog-ca-real">{fmtEur(caReal, 2)}</div>
            </div>
            <div data-testid="rex-prog-ca-fcst-wrap">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <CalendarClock className="h-3.5 w-3.5" style={{ color: STATUT_BAR.provisoire }} />
                CA prévisionnel <span className="opacity-70">· à venir</span>
              </div>
              <div className="mt-0.5 text-lg font-semibold tabular-nums" data-testid="rex-prog-ca-fcst">{fmtEur(caFcst, 2)}</div>
            </div>
          </div>
          {caFcst > 0 && (
            <>
              <Separator orientation="vertical" className="hidden h-11 lg:block" />
              <div data-testid="rex-prog-ca-fcst-range">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <GaugeCircle className="h-3.5 w-3.5" />
                  Fourchette prévisionnelle <span className="opacity-70">· ± 30 %</span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-sm">
                  <Badge variant="outline" className="tabular-nums" data-testid="rex-prog-ca-low">{fmtEur(fcstLo, 2)}</Badge>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <Badge variant="outline" className="tabular-nums" data-testid="rex-prog-ca-high">{fmtEur(fcstHi, 2)}</Badge>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className="card-shadow" data-testid="rex-prog-reliability-summary">
      <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-3 py-4">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: 'rgba(5,150,105,0.12)' }}>
            <ShieldCheck className="h-5 w-5" style={{ color: STATUT_BAR.ferme }} />
          </span>
          <div>
            <div className="text-xs text-muted-foreground">Volume programmé {year}</div>
            <div className="text-xl font-semibold tabular-nums">
              {fmtInt(prog)} <span className="text-sm font-normal text-muted-foreground">bulletins</span>
            </div>
          </div>
        </div>
        <div className="min-w-[240px] flex-1">
          <ReliabilityBar totals={st} height="h-3" />
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {STATUT_ORDER.map((s) => (
              <span key={s} className="flex items-center gap-1.5" data-testid={`rex-prog-reliability-${s}`}>
                <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: STATUT_BAR[s] }} />
                <span className="text-muted-foreground">{STATUT_CFG[s].label}</span>
                <b className="tabular-nums">{fmtInt(st[s] || 0)}</b>
                <span className="text-muted-foreground">({pct(s)}%)</span>
              </span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// --------------------------------------------------------------------------- //
//  Ligne de détail ÉDITABLE (canal × région) — édition « 1 clic » inline        //
//  Chaque cellule mois : clic → saisie → Entrée/blur enregistre et recalcule.   //
// --------------------------------------------------------------------------- //
const CANAUX_FALLBACK = ['Rue Fixe', 'Rue Itinérance', 'Sites Privés', 'Porte-à-Porte'];

// Liste déroulante de ZONE/RÉGION avec création en 1 clic. Les zones existantes sont
// proposées ; si la zone tapée n'existe pas, « + Ajouter … » la crée et la propage
// dans tout le système (POST /rex/programmation/regions → meta rechargé).
const RegionCombobox = ({ value, options, onPick, onRegionsChanged, testid }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const opts = options && options.length ? options : [];
  const query = q.trim();
  const filtered = query
    ? opts.filter((o) => o.toLowerCase().includes(query.toLowerCase()))
    : opts;
  const exactExists = opts.some((o) => o.toLowerCase() === query.toLowerCase());

  const pick = (name) => { onPick?.(name); setOpen(false); setQ(''); };

  const create = async () => {
    const name = query;
    if (!name) return;
    setCreating(true);
    try {
      await api.post('/rex/programmation/regions', { name });
      await onRegionsChanged?.();
      toast.success(`Zone « ${name} » ajoutée — disponible partout`);
      pick(name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ajout impossible');
    }
    setCreating(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-7 w-[158px] items-center justify-between gap-1 rounded-md border bg-background px-2 text-xs hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)]"
          data-testid={testid}
          title="Choisir ou créer une zone / région"
        >
          <span className={`truncate ${value ? '' : 'text-muted-foreground'}`}>{value || 'Zone / région'}</span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Rechercher ou créer une zone…" value={q} onValueChange={setQ} className="text-xs" />
          <CommandList>
            {filtered.length === 0 && !query && <CommandEmpty>Aucune zone.</CommandEmpty>}
            {filtered.length > 0 && (
              <CommandGroup heading="Zones existantes">
                {filtered.map((o) => (
                  <CommandItem key={o} value={o} onSelect={() => pick(o)} className="text-xs" data-testid={`region-opt-${o}`}>
                    <Check className={`mr-2 h-3.5 w-3.5 ${value === o ? 'opacity-100 text-[color:var(--tenant-accent-600)]' : 'opacity-0'}`} />
                    {o}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {query && !exactExists && (
              <CommandGroup heading="Nouvelle zone">
                <CommandItem value={`__create__${query}`} onSelect={create} disabled={creating} className="text-xs" data-testid="region-create-item">
                  {creating ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-2 h-3.5 w-3.5 text-[color:var(--tenant-accent-600)]" />}
                  Ajouter « {query} »
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};


const DetailLineRow = ({ code, year, line, monthsHead, metric = 'volume', canaux, regions, onRegionsChanged, onChanged }) => {
  const [editM, setEditM] = useState(null);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const cleanRegion = line.region === '—' ? '' : (line.region || '');

  const list = (canaux && canaux.length ? canaux : CANAUX_FALLBACK);
  const canalVal = list.includes(line.canal) ? line.canal : undefined;

  const op = async (body) => {
    setBusy(true);
    try {
      await api.post('/rex/programmation/line-op', { association_code: code, year, line_id: line.id, ...body });
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
    setBusy(false);
  };

  const saveCell = async (m) => {
    const raw = String(val).trim();
    setEditM(null);
    const cur = Number((line.months || {})[String(m)] || 0);
    const nv = raw === '' ? 0 : Math.round(Number(raw));
    if (isNaN(nv) || nv === cur) return;
    await op({ op: 'set_cell', month: m, value: nv });
  };

  return (
    <TableRow className="bg-muted/20" data-testid={`rex-prog-detail-${code}-${line.id}`}>
      <TableCell className="sticky left-0 z-20 bg-background shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">
        <div className="flex flex-wrap items-center gap-1.5 pl-9">
          <Select value={canalVal} onValueChange={(v) => op({ op: 'set_meta', canal: v })}>
            <SelectTrigger className="h-7 w-[148px] text-xs" data-testid={`rex-prog-detail-canal-${code}-${line.id}`}>
              <SelectValue placeholder="Canal…" />
            </SelectTrigger>
            <SelectContent>
              {list.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <RegionCombobox
            value={cleanRegion}
            options={regions}
            onPick={(name) => { if ((name || '').trim() !== cleanRegion) op({ op: 'set_meta', region: name }); }}
            onRegionsChanged={onRegionsChanged}
            testid={`rex-prog-detail-region-${code}-${line.id}`}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="focus-visible:outline-none" data-testid={`rex-prog-detail-statut-${code}-${line.id}`} title="Changer la fiabilité">
                <Badge variant="outline" className={`h-5 cursor-pointer px-1.5 text-[9px] font-normal ${STATUT_CFG[line.statut]?.cls || ''}`}>
                  {STATUT_CFG[line.statut]?.label || line.statut}
                  <ChevronDown className="ml-0.5 h-2.5 w-2.5" />
                </Badge>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {STATUT_ORDER.map((s) => (
                <DropdownMenuItem key={s} onClick={() => op({ op: 'set_meta', statut: s })} data-testid={`rex-prog-detail-set-statut-${code}-${line.id}-${s}`}>
                  <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ background: STATUT_BAR[s] }} />
                  {STATUT_CFG[s].label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {metric === 'revenue' && (
            <span className="flex items-center gap-1 text-[11px] text-muted-foreground" title="Prix convenu pour ce canal (modifiable via « Prix »)">
              {line.price ? (
                <span className="tabular-nums font-medium text-foreground">{fmtEur(line.price, 2)}<span className="font-normal text-muted-foreground">/BS</span></span>
              ) : (
                <span className="rounded border border-dashed border-amber-400 px-1.5 py-0.5 text-amber-600 dark:text-amber-400">prix à définir</span>
              )}
            </span>
          )}
        </div>
      </TableCell>
      {(monthsHead || []).map((m) => {
        if (metric === 'revenue') {
          const rv = Number((line.revenue_months || {})[String(m.num)] || 0);
          return (
            <TableCell
              key={m.num}
              className="text-center text-xs tabular-nums text-muted-foreground"
              data-testid={`rex-prog-detail-cell-${code}-${line.id}-${m.num}`}
            >
              {rv ? fmtEur(rv, 2) : <span className="opacity-40">·</span>}
            </TableCell>
          );
        }
        const isEd = editM === m.num;
        const v = Number((line.months || {})[String(m.num)] || 0);
        return (
          <TableCell
            key={m.num}
            className={`text-center text-xs tabular-nums ${isEd ? '' : 'cursor-pointer hover:bg-[color:var(--tenant-accent-050)] dark:hover:bg-muted/40'}`}
            onClick={() => { if (!isEd) { setEditM(m.num); setVal(v ? String(v) : ''); } }}
            data-testid={`rex-prog-detail-cell-${code}-${line.id}-${m.num}`}
          >
            {isEd ? (
              <Input
                autoFocus
                type="number"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                onBlur={() => saveCell(m.num)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveCell(m.num); if (e.key === 'Escape') setEditM(null); }}
                className="h-7 w-full px-1 text-center text-xs"
                data-testid={`rex-prog-detail-input-${code}-${line.id}-${m.num}`}
              />
            ) : (v ? fmtInt(v) : <span className="text-muted-foreground opacity-40">·</span>)}
          </TableCell>
        );
      })}
      <TableCell className="text-right text-xs font-semibold tabular-nums" style={metric === 'revenue' ? { color: 'var(--tenant-accent-600)' } : {}}>
        {metric === 'revenue' ? fmtEur(line.revenue_total, 2) : fmtInt(line.total)}
      </TableCell>
      <TableCell />
      <TableCell className="px-1 text-center">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-rose-500 hover:text-rose-700"
          disabled={busy}
          onClick={() => op({ op: 'delete' })}
          data-testid={`rex-prog-detail-del-${code}-${line.id}`}
          title="Supprimer la ligne"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </TableCell>
    </TableRow>
  );
};


// --------------------------------------------------------------------------- //
//  Vue « Par canal » — même matrice, regroupée Canal → Région → Association     //
//  Construite côté client depuis le détail (lines) renvoyé par /matrix.         //
// --------------------------------------------------------------------------- //
const CANAL_ORDER = ['Porte-à-Porte', 'Rue Fixe', 'Rue Itinérance', 'Sites Privés'];

// Canonicalise les libellés de région pour éviter les doublons dus aux variantes
// d'écriture (accents, tirets vs espaces, casse) — ex. « Nouvelle Aquitaine » et
// « Nouvelle-Aquitaine » sont fusionnées sur une seule ligne.
const REGION_CANON = {
  'ile de france': 'Île-de-France',
  'paris idf': 'Île-de-France',
  'paris et idf': 'Île-de-France',
  'paris idf idf': 'Île-de-France',
  'idf': 'Île-de-France',
  'paris': 'Île-de-France',
  'nouvelle aquitaine': 'Nouvelle-Aquitaine',
  'provence alpes cote d azur': "Provence-Alpes-Côte d'Azur",
  'paca': "Provence-Alpes-Côte d'Azur",
  'auvergne rhone alpes': 'Auvergne-Rhône-Alpes',
  'grand est': 'Grand Est',
  'centre val de loire': 'Centre-Val de Loire',
  'bourgogne franche comte': 'Bourgogne-Franche-Comté',
  'hauts de france': 'Hauts-de-France',
  'pays de la loire': 'Pays de la Loire',
  'occitanie': 'Occitanie',
  'bretagne': 'Bretagne',
  'normandie': 'Normandie',
  'corse': 'Corse',
};
const regionKey = (s) => (s || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
// Libellé d'affichage canonique pour une clé de région (sinon le brut fourni).
const regionLabel = (raw) => {
  const r = (raw || '').trim();
  if (!r) return '—';
  return REGION_CANON[regionKey(r)] || r;
};

const CanalMatrix = ({ rows, monthsHead, year }) => {
  const [openCanal, setOpenCanal] = useState({});
  const [openRegion, setOpenRegion] = useState({});

  const groups = useMemo(() => {
    const zero = () => { const o = {}; for (let m = 1; m <= 12; m += 1) o[m] = 0; return o; };
    const addS = (obj, s, v) => { obj[s] = (obj[s] || 0) + v; };
    const byCanal = {};
    (rows || []).forEach((row) => {
      (row.lines || []).forEach((ln) => {
        const canal = ln.canal || '—';
        const rkey = regionKey(ln.region) || '—';
        const c = byCanal[canal] || (byCanal[canal] = { canal, months: zero(), total: 0, status: {}, regions: {} });
        const r = c.regions[rkey] || (c.regions[rkey] = { region: regionLabel(ln.region), months: zero(), total: 0, status: {}, assocs: {} });
        const a = r.assocs[row.code] || (r.assocs[row.code] = { code: row.code, name: row.name, is_prospect: row.is_prospect, months: zero(), total: 0, status: {} });
        (monthsHead || []).forEach((m) => {
          const v = Number((ln.months || {})[String(m.num)] || 0);
          c.months[m.num] += v; r.months[m.num] += v; a.months[m.num] += v;
        });
        const t = Number(ln.total || 0);
        c.total += t; r.total += t; a.total += t;
        addS(c.status, ln.statut, t); addS(r.status, ln.statut, t); addS(a.status, ln.statut, t);
      });
    });
    const ord = (name) => { const i = CANAL_ORDER.indexOf(name); return i === -1 ? 99 : i; };
    const arr = Object.values(byCanal).map((c) => ({
      ...c,
      regions: Object.values(c.regions).map((r) => ({
        ...r, assocs: Object.values(r.assocs).sort((x, y) => y.total - x.total),
      })).sort((x, y) => y.total - x.total),
    })).sort((x, y) => (ord(x.canal) - ord(y.canal)) || (y.total - x.total));
    const gm = zero(); let gt = 0; const gs = {};
    arr.forEach((c) => {
      (monthsHead || []).forEach((m) => { gm[m.num] += c.months[m.num]; });
      gt += c.total;
      STATUT_ORDER.forEach((s) => { gs[s] = (gs[s] || 0) + (c.status[s] || 0); });
    });
    return { arr, gm, gt, gs };
  }, [rows, monthsHead]);

  const Num = ({ v, cls = '' }) => (
    <TableCell className={`text-center tabular-nums ${cls}`}>
      {v ? fmtInt(v) : <span className="text-muted-foreground opacity-40">·</span>}
    </TableCell>
  );

  if (!groups.arr.length) {
    return (
      <div className="p-10 text-center text-sm text-muted-foreground" data-testid="rex-prog-canal-empty">
        Aucun détail par canal pour {year}. Renseignez le détail (canal × région) via
        « Modifier le détail » sur une association, puis revenez ici.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table className="text-sm">
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="sticky left-0 z-20 min-w-[230px] bg-muted shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">Canal / Région / Association</TableHead>
            {(monthsHead || []).map((m) => (
              <TableHead key={m.num} className="min-w-[64px] text-center capitalize">{MOIS_ABBR[m.num - 1]}</TableHead>
            ))}
            <TableHead className="min-w-[84px] text-right font-semibold">Total {year}</TableHead>
            <TableHead className="min-w-[150px] text-center">Fiabilité</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.arr.map((c) => { const cOpen = !!openCanal[c.canal]; return (
            <React.Fragment key={c.canal}>
              <TableRow className="bg-sky-50/60 dark:bg-sky-950/20" data-testid={`rex-prog-canal-${c.canal}`}>
                <TableCell className="sticky left-0 z-20 bg-sky-50 font-semibold shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)] dark:bg-sky-950">
                  <button
                    onClick={() => setOpenCanal((o) => ({ ...o, [c.canal]: !o[c.canal] }))}
                    className="flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)]"
                    aria-expanded={cOpen}
                    data-testid={`rex-prog-canal-toggle-${c.canal}`}
                  >
                    <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${cOpen ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} />
                    {c.canal}
                  </button>
                </TableCell>
                {(monthsHead || []).map((m) => (<Num key={m.num} v={c.months[m.num]} cls="font-semibold" />))}
                <TableCell className="text-right font-semibold tabular-nums">{fmtInt(c.total)}</TableCell>
                <TableCell className="px-2"><ReliabilityBar totals={c.status} /></TableCell>
              </TableRow>
              {cOpen && c.regions.map((r) => { const rKey = `${c.canal}|${r.region}`; const rOpen = !!openRegion[rKey]; return (
                <React.Fragment key={rKey}>
                  <TableRow className="bg-muted/20" data-testid={`rex-prog-region-${rKey}`}>
                    <TableCell className="sticky left-0 z-20 bg-background shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">
                      <button
                        onClick={() => setOpenRegion((o) => ({ ...o, [rKey]: !o[rKey] }))}
                        className="flex items-center gap-1.5 whitespace-nowrap pl-6 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)]"
                        aria-expanded={rOpen}
                        data-testid={`rex-prog-region-toggle-${rKey}`}
                      >
                        <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${rOpen ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} />
                        {r.region}
                      </button>
                    </TableCell>
                    {(monthsHead || []).map((m) => (<Num key={m.num} v={r.months[m.num]} cls="text-xs" />))}
                    <TableCell className="text-right text-xs font-medium tabular-nums">{fmtInt(r.total)}</TableCell>
                    <TableCell className="px-2"><ReliabilityBar totals={r.status} height="h-1.5" /></TableCell>
                  </TableRow>
                  {rOpen && r.assocs.map((a) => (
                    <TableRow key={`${rKey}|${a.code}`} className="bg-background" data-testid={`rex-prog-canal-assoc-${rKey}-${a.code}`}>
                      <TableCell className="sticky left-0 z-20 bg-background shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">
                        <div className="flex items-center gap-1.5 pl-12 text-xs text-muted-foreground">
                          <span>{a.name}</span>
                          {a.is_prospect && <Badge variant="outline" className="h-4 px-1 text-[9px] font-normal">prospect</Badge>}
                        </div>
                      </TableCell>
                      {(monthsHead || []).map((m) => (<Num key={m.num} v={a.months[m.num]} cls="text-xs" />))}
                      <TableCell className="text-right text-xs tabular-nums">{fmtInt(a.total)}</TableCell>
                      <TableCell className="px-2"><ReliabilityBar totals={a.status} height="h-1.5" /></TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ); })}
            </React.Fragment>
          ); })}
          <TableRow className="border-t-2 bg-muted/40 font-semibold">
            <TableCell className="sticky left-0 z-20 bg-muted shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">TOTAL consolidé</TableCell>
            {(monthsHead || []).map((m) => (
              <TableCell key={m.num} className="text-center tabular-nums">{fmtInt(groups.gm[m.num])}</TableCell>
            ))}
            <TableCell className="text-right tabular-nums" style={{ color: 'var(--tenant-accent-600)' }}>{fmtInt(groups.gt)}</TableCell>
            <TableCell className="px-2"><ReliabilityBar totals={groups.gs} /></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};


// --------------------------------------------------------------------------- //
//  Vue « Par zone » — même matrice, regroupée Zone → Canal → Association        //
//  Objectif : anticiper logistique / recrutement / responsables par territoire. //
//  Construite côté client depuis le détail (lines) renvoyé par /matrix.         //
// --------------------------------------------------------------------------- //
const RegionMatrix = ({ rows, monthsHead, year }) => {
  const [openRegion, setOpenRegion] = useState({});
  const [openCanal, setOpenCanal] = useState({});

  const groups = useMemo(() => {
    const zero = () => { const o = {}; for (let m = 1; m <= 12; m += 1) o[m] = 0; return o; };
    const addS = (obj, s, v) => { obj[s] = (obj[s] || 0) + v; };
    const cOrd = (name) => { const i = CANAL_ORDER.indexOf(name); return i === -1 ? 99 : i; };
    const byRegion = {};
    (rows || []).forEach((row) => {
      (row.lines || []).forEach((ln) => {
        const rkey = regionKey(ln.region) || '—';
        const canal = ln.canal || '—';
        const r = byRegion[rkey] || (byRegion[rkey] = { region: regionLabel(ln.region), months: zero(), total: 0, status: {}, nbAssocs: new Set(), canaux: {} });
        const c = r.canaux[canal] || (r.canaux[canal] = { canal, months: zero(), total: 0, status: {}, assocs: {} });
        const a = c.assocs[row.code] || (c.assocs[row.code] = { code: row.code, name: row.name, is_prospect: row.is_prospect, months: zero(), total: 0, status: {} });
        (monthsHead || []).forEach((m) => {
          const v = Number((ln.months || {})[String(m.num)] || 0);
          r.months[m.num] += v; c.months[m.num] += v; a.months[m.num] += v;
        });
        const t = Number(ln.total || 0);
        r.total += t; c.total += t; a.total += t;
        r.nbAssocs.add(row.code);
        addS(r.status, ln.statut, t); addS(c.status, ln.statut, t); addS(a.status, ln.statut, t);
      });
    });
    const arr = Object.values(byRegion).map((r) => ({
      ...r,
      nbAssocs: r.nbAssocs.size,
      canaux: Object.values(r.canaux).map((c) => ({
        ...c, assocs: Object.values(c.assocs).sort((x, y) => y.total - x.total),
      })).sort((x, y) => (cOrd(x.canal) - cOrd(y.canal)) || (y.total - x.total)),
    })).sort((x, y) => y.total - x.total);
    const gm = zero(); let gt = 0; const gs = {};
    arr.forEach((r) => {
      (monthsHead || []).forEach((m) => { gm[m.num] += r.months[m.num]; });
      gt += r.total;
      STATUT_ORDER.forEach((s) => { gs[s] = (gs[s] || 0) + (r.status[s] || 0); });
    });
    return { arr, gm, gt, gs };
  }, [rows, monthsHead]);

  const Num = ({ v, cls = '' }) => (
    <TableCell className={`text-center tabular-nums ${cls}`}>
      {v ? fmtInt(v) : <span className="text-muted-foreground opacity-40">·</span>}
    </TableCell>
  );

  if (!groups.arr.length) {
    return (
      <div className="p-10 text-center text-sm text-muted-foreground" data-testid="rex-prog-zone-empty">
        Aucun détail par zone pour {year}. Renseignez le détail (canal × région) via
        « Modifier le détail » sur une association, puis revenez ici.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table className="text-sm">
        <TableHeader>
          <TableRow className="bg-muted/50">
            <TableHead className="sticky left-0 z-20 min-w-[250px] bg-muted shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">Zone / Canal / Association</TableHead>
            {(monthsHead || []).map((m) => (
              <TableHead key={m.num} className="min-w-[64px] text-center capitalize">{MOIS_ABBR[m.num - 1]}</TableHead>
            ))}
            <TableHead className="min-w-[84px] text-right font-semibold">Total {year}</TableHead>
            <TableHead className="min-w-[150px] text-center">Fiabilité</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.arr.map((r) => { const rOpen = !!openRegion[r.region]; return (
            <React.Fragment key={r.region}>
              <TableRow className="bg-emerald-50/60 dark:bg-emerald-950/20" data-testid={`rex-prog-zone-${r.region}`}>
                <TableCell className="sticky left-0 z-20 bg-emerald-50 font-semibold shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)] dark:bg-emerald-950">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => setOpenRegion((o) => ({ ...o, [r.region]: !o[r.region] }))}
                        className="flex w-full items-center gap-1.5 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)]"
                        aria-expanded={rOpen}
                        data-testid={`rex-prog-zone-toggle-${r.region}`}
                      >
                        <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${rOpen ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} />
                        <MapPin className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        <span className="truncate">{r.region}</span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      {r.nbAssocs} association{r.nbAssocs > 1 ? 's' : ''} · {r.canaux.length} {r.canaux.length > 1 ? 'canaux' : 'canal'} déployé{r.canaux.length > 1 ? 's' : ''}
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                {(monthsHead || []).map((m) => (<Num key={m.num} v={r.months[m.num]} cls="font-semibold" />))}
                <TableCell className="text-right font-semibold tabular-nums">{fmtInt(r.total)}</TableCell>
                <TableCell className="px-2"><ReliabilityBar totals={r.status} /></TableCell>
              </TableRow>
              {rOpen && r.canaux.map((c) => { const cKey = `${r.region}|${c.canal}`; const cOpen = !!openCanal[cKey]; return (
                <React.Fragment key={cKey}>
                  <TableRow className="bg-muted/20" data-testid={`rex-prog-zone-canal-${cKey}`}>
                    <TableCell className="sticky left-0 z-20 bg-background shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">
                      <button
                        onClick={() => setOpenCanal((o) => ({ ...o, [cKey]: !o[cKey] }))}
                        className="flex items-center gap-1.5 whitespace-nowrap pl-6 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--tenant-accent-500)]"
                        aria-expanded={cOpen}
                        data-testid={`rex-prog-zone-canal-toggle-${cKey}`}
                      >
                        <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${cOpen ? 'rotate-90 text-[color:var(--tenant-accent-600)]' : ''}`} />
                        <Layers className="h-3 w-3 shrink-0 text-muted-foreground" />
                        {c.canal}
                      </button>
                    </TableCell>
                    {(monthsHead || []).map((m) => (<Num key={m.num} v={c.months[m.num]} cls="text-xs" />))}
                    <TableCell className="text-right text-xs font-medium tabular-nums">{fmtInt(c.total)}</TableCell>
                    <TableCell className="px-2"><ReliabilityBar totals={c.status} height="h-1.5" /></TableCell>
                  </TableRow>
                  {cOpen && c.assocs.map((a) => (
                    <TableRow key={`${cKey}|${a.code}`} className="bg-background" data-testid={`rex-prog-zone-assoc-${cKey}-${a.code}`}>
                      <TableCell className="sticky left-0 z-20 bg-background shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">
                        <div className="flex items-center gap-1.5 whitespace-nowrap pl-12 text-xs text-muted-foreground">
                          <Building2 className="h-3 w-3 shrink-0" />
                          <span className="truncate">{a.name}</span>
                          {a.is_prospect && <Badge variant="outline" className="h-4 px-1 text-[9px] font-normal">prospect</Badge>}
                        </div>
                      </TableCell>
                      {(monthsHead || []).map((m) => (<Num key={m.num} v={a.months[m.num]} cls="text-xs" />))}
                      <TableCell className="text-right text-xs tabular-nums">{fmtInt(a.total)}</TableCell>
                      <TableCell className="px-2"><ReliabilityBar totals={a.status} height="h-1.5" /></TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ); })}
            </React.Fragment>
          ); })}
          <TableRow className="border-t-2 bg-muted/40 font-semibold">
            <TableCell className="sticky left-0 z-20 bg-muted shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">TOTAL consolidé</TableCell>
            {(monthsHead || []).map((m) => (
              <TableCell key={m.num} className="text-center tabular-nums">{fmtInt(groups.gm[m.num])}</TableCell>
            ))}
            <TableCell className="text-right tabular-nums" style={{ color: 'var(--tenant-accent-600)' }}>{fmtInt(groups.gt)}</TableCell>
            <TableCell className="px-2"><ReliabilityBar totals={groups.gs} /></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};


// --------------------------------------------------------------------------- //
//  Vue Comparaison Réalisé vs Programmé                                        //
// --------------------------------------------------------------------------- //
const StatCard = ({ label, value, tone, ecart }) => {
  let color = '#64748b';
  if (tone === 'programmed') color = '#EE722E';
  else if (tone === 'ecart') color = value >= 0 ? '#059669' : '#dc2626';
  return (
    <Card className="card-shadow">
      <CardContent className="py-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tabular-nums" style={{ color }}>
          {ecart && value > 0 ? '+' : ''}{fmtInt(value)}
        </div>
      </CardContent>
    </Card>
  );
};

const EcartCell = ({ value, muted }) => {
  if (muted) return <span className="tabular-nums text-muted-foreground">{value ? fmtInt(value) : '·'}</span>;
  if (!value) return <span className="tabular-nums text-muted-foreground">·</span>;
  const pos = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 tabular-nums ${pos ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
      {pos ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {pos ? '+' : ''}{fmtInt(value)}
    </span>
  );
};

// Détecte un retard : à périmètre de mois ÉCOULÉS, réalisé nettement sous le programmé.
const computeLag = (row, elapsed) => {
  if (!elapsed || elapsed.length === 0) return null;
  let r = 0;
  let p = 0;
  elapsed.forEach((m) => {
    r += Number((row.realized || {})[String(m)] || 0);
    p += Number((row.programmed || {})[String(m)] || 0);
  });
  if (p <= 0) return null;                 // rien de programmé sur la période → pas de retard
  const ratio = r / p;
  if (ratio >= 0.8) return null;           // dans les clous
  return { pct: Math.round((1 - ratio) * 100), ytdR: r, ytdP: p };
};

const LagBadge = ({ lag }) => {
  if (!lag) return null;
  return (
    <Badge
      variant="outline"
      className="ml-1.5 h-5 gap-0.5 border-rose-300 px-1.5 text-[10px] font-medium text-rose-600 dark:border-rose-800 dark:text-rose-400"
      title={`Réalisé à date (${fmtInt(lag.ytdR)}) sous le programmé à date (${fmtInt(lag.ytdP)})`}
    >
      <TrendingDown className="h-3 w-3" />En retard −{lag.pct}%
    </Badge>
  );
};

// Tableau Réalisé vs Programmé groupé par une dimension (canal ou zone), dépliable au mois.
const BreakdownTable = ({ title, colLabel, icon: Icon, rows, testid, elapsed }) => {
  const [open, setOpen] = useState(null);
  if (!rows || rows.length === 0) return null;
  return (
    <Card className="card-shadow" data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {Icon && <Icon className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />}{title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table className="text-sm">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="min-w-[220px]">{colLabel}</TableHead>
              <TableHead className="text-right">Réalisé</TableHead>
              <TableHead className="text-right" style={{ color: '#EE722E' }}>Programmé</TableHead>
              <TableHead className="text-right">Écart</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const isOpen = open === r.key;
              return (
                <React.Fragment key={r.key}>
                  <TableRow className="cursor-pointer" onClick={() => setOpen(isOpen ? null : r.key)} data-testid={`${testid}-row-${r.key}`}>
                    <TableCell className="font-medium">
                      <span className="flex items-center gap-1.5">
                        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        {r.key}
                        <LagBadge lag={computeLag(r, elapsed)} />
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtInt(r.realized_total)}</TableCell>
                    <TableCell className="text-right tabular-nums" style={{ color: '#EE722E' }}>{fmtInt(r.programmed_total)}</TableCell>
                    <TableCell className="text-right"><EcartCell value={r.ecart_total} muted={r.realized_total === 0 && r.programmed_total > 0} /></TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow>
                      <TableCell colSpan={4} className="bg-muted/20 p-0">
                        <div className="overflow-x-auto p-2">
                          <Table className="text-xs">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="min-w-[90px]">Mois</TableHead>
                                {MOIS_ABBR.map((m, i) => <TableHead key={i} className="min-w-[52px] text-center capitalize">{m}</TableHead>)}
                                <TableHead className="text-right">Total</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell className="font-medium">Réalisé</TableCell>
                                {MOIS_ABBR.map((_, i) => <TableCell key={i} className="text-center tabular-nums">{r.realized[String(i + 1)] ? fmtInt(r.realized[String(i + 1)]) : '·'}</TableCell>)}
                                <TableCell className="text-right font-semibold tabular-nums">{fmtInt(r.realized_total)}</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell className="font-medium" style={{ color: '#EE722E' }}>Programmé</TableCell>
                                {MOIS_ABBR.map((_, i) => <TableCell key={i} className="text-center tabular-nums">{r.programmed[String(i + 1)] ? fmtInt(r.programmed[String(i + 1)]) : '·'}</TableCell>)}
                                <TableCell className="text-right font-semibold tabular-nums" style={{ color: '#EE722E' }}>{fmtInt(r.programmed_total)}</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// Mini-tableau compact (totaux) d'une dimension pour une association donnée.
const MiniBreakdown = ({ title, label, rows, elapsed, testid }) => {
  if (!rows || rows.length === 0) {
    return <div className="rounded-lg border p-2 text-[11px] text-muted-foreground" data-testid={testid}>{title} — aucune donnée</div>;
  }
  return (
    <div className="overflow-hidden rounded-lg border" data-testid={testid}>
      <div className="border-b bg-muted/40 px-2.5 py-1.5 text-[11px] font-semibold">{title}</div>
      <Table className="text-[11px]">
        <TableHeader>
          <TableRow>
            <TableHead className="h-7 px-2.5">{label}</TableHead>
            <TableHead className="h-7 px-2 text-right">Réalisé</TableHead>
            <TableHead className="h-7 px-2 text-right" style={{ color: '#EE722E' }}>Prog.</TableHead>
            <TableHead className="h-7 px-2.5 text-right">Écart</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} data-testid={`${testid}-row-${r.key}`}>
              <TableCell className="px-2.5 py-1 font-medium"><span className="flex items-center">{r.key}<LagBadge lag={computeLag(r, elapsed)} /></span></TableCell>
              <TableCell className="px-2 py-1 text-right tabular-nums">{fmtInt(r.realized_total)}</TableCell>
              <TableCell className="px-2 py-1 text-right tabular-nums" style={{ color: '#EE722E' }}>{fmtInt(r.programmed_total)}</TableCell>
              <TableCell className="px-2.5 py-1 text-right"><EcartCell value={r.ecart_total} muted={r.realized_total === 0 && r.programmed_total > 0} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const ComparisonView = ({ year }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAssoc, setOpenAssoc] = useState(null);

  const load = useCallback(async () => {
    if (!year) return;
    setLoading(true);
    try {
      const { data: d } = await api.get('/rex/programmation/comparison', { params: { year } });
      setData(d);
    } catch (e) { toast.error('Chargement de la comparaison impossible'); }
    setLoading(false);
  }, [year]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-72 w-full" /></div>;
  }
  if (!data) return null;

  const cons = data.consolidated;
  const chartData = data.months.map((m) => ({
    mois: MOIS_ABBR[m.month - 1], Réalisé: m.realized, Programmé: m.programmed,
  }));
  const futureOnly = cons.realized_total === 0 && cons.programmed_total > 0;
  // Mois écoulés (passés) — base de la détection « en retard » à date.
  const elapsed = (data.months || []).filter((m) => m.type === 'past').map((m) => m.month);

  return (
    <div className="space-y-4" data-testid="rex-prog-comparison">
      {futureOnly && (
        <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Année <b>{data.year}</b> encore à venir : le <b>réalisé</b> est à 0 et se remplira automatiquement au fil des mois. L'écart affiche donc, pour l'instant, la totalité du volume à réaliser.</span>
        </div>
      )}

      {/* Cartes de synthèse */}
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label={`Réalisé ${data.year}`} value={cons.realized_total} tone="realized" />
        <StatCard label={`Programmé ${data.year}`} value={cons.programmed_total} tone="programmed" />
        <StatCard label="Écart (Réel − Programmé)" value={cons.ecart_total} tone="ecart" ecart />
      </div>

      {/* Graphe */}
      <Card className="card-shadow">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="h-4 w-4" style={{ color: 'var(--tenant-accent-600)' }} />
            Réalisé vs Programmé — {data.year}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }} barGap={2}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,0.25)" />
                <XAxis dataKey="mois" tick={{ fontSize: 11 }} tickLine={false} axisLine={{ stroke: 'rgba(148,163,184,0.4)' }} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={48} />
                <RTooltip
                  cursor={{ fill: 'rgba(148,163,184,0.12)' }}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid rgba(148,163,184,0.3)' }}
                  formatter={(v, n) => [fmtInt(v), n]}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Réalisé" fill="#64748b" radius={[3, 3, 0, 0]} maxBarSize={22} />
                <Bar dataKey="Programmé" fill="#EE722E" radius={[3, 3, 0, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Table consolidée mois par mois */}
      <Card className="card-shadow">
        <CardHeader className="pb-2"><CardTitle className="text-base">Détail mensuel consolidé</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-sm">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="sticky left-0 z-20 min-w-[140px] bg-muted shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">Indicateur</TableHead>
                  {MOIS_ABBR.map((m, i) => <TableHead key={i} className="min-w-[62px] text-center capitalize">{m}</TableHead>)}
                  <TableHead className="min-w-[84px] text-right font-semibold">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="sticky left-0 z-20 bg-background font-medium shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]">Réalisé</TableCell>
                  {data.months.map((m) => (
                    <TableCell key={m.month} className="text-center tabular-nums">{m.realized ? fmtInt(m.realized) : <span className="text-muted-foreground">·</span>}</TableCell>
                  ))}
                  <TableCell className="text-right font-semibold tabular-nums">{fmtInt(cons.realized_total)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="sticky left-0 z-20 bg-background font-medium shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)]" style={{ color: '#EE722E' }}>Programmé</TableCell>
                  {data.months.map((m) => (
                    <TableCell key={m.month} className="text-center tabular-nums">{m.programmed ? fmtInt(m.programmed) : <span className="text-muted-foreground">·</span>}</TableCell>
                  ))}
                  <TableCell className="text-right font-semibold tabular-nums" style={{ color: '#EE722E' }}>{fmtInt(cons.programmed_total)}</TableCell>
                </TableRow>
                <TableRow className="border-t bg-muted/30">
                  <TableCell className="sticky left-0 z-20 bg-muted shadow-[6px_0_10px_-8px_rgba(0,0,0,0.18)] font-medium">Écart</TableCell>
                  {data.months.map((m) => (
                    <TableCell key={m.month} className="text-center"><EcartCell value={m.ecart} muted={m.type === 'future'} /></TableCell>
                  ))}
                  <TableCell className="text-right font-semibold"><EcartCell value={cons.ecart_total} muted={futureOnly} /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Détail par association */}
      <Card className="card-shadow">
        <CardHeader className="pb-2"><CardTitle className="text-base">Par association</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table className="text-sm">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="min-w-[220px]">Association</TableHead>
                <TableHead className="text-right">Réalisé</TableHead>
                <TableHead className="text-right" style={{ color: '#EE722E' }}>Programmé</TableHead>
                <TableHead className="text-right">Écart</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.associations.map((a) => {
                const open = openAssoc === a.code;
                return (
                  <React.Fragment key={a.code}>
                    <TableRow
                      className="cursor-pointer"
                      onClick={() => setOpenAssoc(open ? null : a.code)}
                      data-testid={`rex-prog-cmp-row-${a.code}`}
                    >
                      <TableCell className="font-medium">
                        <span className="flex items-center gap-1.5">
                          <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
                          {a.name}
                          {a.is_prospect && <Badge variant="outline" className="h-5 px-1.5 text-[10px] font-normal text-muted-foreground">prospect</Badge>}
                          <LagBadge lag={computeLag(a, elapsed)} />
                        </span>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmtInt(a.realized_total)}</TableCell>
                      <TableCell className="text-right tabular-nums" style={{ color: '#EE722E' }}>{fmtInt(a.programmed_total)}</TableCell>
                      <TableCell className="text-right"><EcartCell value={a.ecart_total} muted={a.realized_total === 0 && a.programmed_total > 0} /></TableCell>
                    </TableRow>
                    {open && (
                      <TableRow>
                        <TableCell colSpan={4} className="bg-muted/20 p-0">
                          <div className="space-y-3 p-2">
                            <div className="overflow-x-auto">
                              <Table className="text-xs">
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="min-w-[90px]">Mois</TableHead>
                                    {MOIS_ABBR.map((m, i) => <TableHead key={i} className="min-w-[52px] text-center capitalize">{m}</TableHead>)}
                                    <TableHead className="text-right">Total</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  <TableRow>
                                    <TableCell className="font-medium">Réalisé</TableCell>
                                    {MOIS_ABBR.map((_, i) => <TableCell key={i} className="text-center tabular-nums">{a.realized[String(i + 1)] ? fmtInt(a.realized[String(i + 1)]) : '·'}</TableCell>)}
                                    <TableCell className="text-right font-semibold tabular-nums">{fmtInt(a.realized_total)}</TableCell>
                                  </TableRow>
                                  <TableRow>
                                    <TableCell className="font-medium" style={{ color: '#EE722E' }}>Programmé</TableCell>
                                    {MOIS_ABBR.map((_, i) => <TableCell key={i} className="text-center tabular-nums">{a.programmed[String(i + 1)] ? fmtInt(a.programmed[String(i + 1)]) : '·'}</TableCell>)}
                                    <TableCell className="text-right font-semibold tabular-nums" style={{ color: '#EE722E' }}>{fmtInt(a.programmed_total)}</TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </div>
                            {/* Détail par canal et par zone pour CETTE association */}
                            <div className="grid gap-3 lg:grid-cols-2">
                              <MiniBreakdown title="Par canal" label="Canal" rows={a.by_canal} elapsed={elapsed} testid={`rex-prog-cmp-${a.code}-canal`} />
                              <MiniBreakdown title="Par zone" label="Zone / Région" rows={a.by_region} elapsed={elapsed} testid={`rex-prog-cmp-${a.code}-region`} />
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Détail par canal — pour voir quel canal avance ou est en retard */}
      <BreakdownTable
        title="Par canal"
        colLabel="Canal"
        icon={Layers}
        rows={data.by_canal}
        elapsed={elapsed}
        testid="rex-prog-cmp-canal"
      />

      {/* Détail par zone / région */}
      <BreakdownTable
        title="Par zone"
        colLabel="Zone / Région"
        icon={MapPin}
        rows={data.by_region}
        elapsed={elapsed}
        testid="rex-prog-cmp-region"
      />
    </div>
  );
};
